import React from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  IconButton,
  Menu,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AutoAwesome as AIIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TuneIcon from "@mui/icons-material/Tune";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { nanoid } from "nanoid";
import {
  useBeforeUnload,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  appendRevision,
  copySplitIntoPlan,
  createPlan,
  createVersionDraft,
  deleteEditorStaging,
  deleteSplit,
  deleteExercise,
  duplicateSplit,
  getEditorStaging,
  getPlanAssignmentCoverage,
  getPublishImpactPreview,
  getPlanTypeLabel,
  getAssignableClients,
  getExerciseCatalog,
  getPlanById,
  getSplitExercises,
  getSplits,
  reorderSplits,
  reorderExercises,
  saveEditorStaging,
  savePlanMetadata,
  publishStagingSnapshotToLive,
  publishVersionDraft,
  trackTrainingPlansV2Event,
  bulkDeleteExercises,
  bulkUpsertExercises,
  upsertExercise,
  upsertSplit,
} from "./services/trainingPlansV2Service";
import { normalizePlanLifecycleStatus } from "./services/planLifecycleStatus";
import { useTrainingPlansV2Hints } from "./useTrainingPlansV2Hints";
import CopyWorkoutFromPlanDialog from "./CopyWorkoutFromPlanDialog";
import { WorkoutTypes } from "../../../../data/Types";
import { getChatGPTResponse } from "../../../../data/GlobalData";
import useEntitlements from "../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../components/organisms/UpgradePrompt";
import { importTrainingPlan } from "../../../../data/TrainingPlanImportData";
import {
  TRAINING_PLAN_IMPORT_SOURCES,
  TRAINING_PLAN_IMPORT_SOURCE_STORAGE_KEY,
  loadStoredTrainingPlanImportSource,
  normalizeTrainingPlanImportSource,
} from "../../../../data/TrainingPlanImportSources";
import { withGuidedSearchPreserve } from "./trainingPlansV2GuidedTaskParams";
import {
  useCreateCustomExerciseBigQuery,
  useExercisesBigQuery,
} from "../../../../hooks/useExercisesBigQuery";
import ExerciseSearchAutocomplete from "../../../../components/molecules/ExerciseSearchAutocomplete";
import LabelSelect from "../../../../components/molecules/LabelSelect";
import {
  DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
  ExercisePrescriptionTypes,
} from "data/exercisePrescription";

const prescriptionLabelByType = {
  [ExercisePrescriptionTypes.REPETITION]: "Reps / Sets",
  [ExercisePrescriptionTypes.DURATION]: "Timed (Duration)",
  [ExercisePrescriptionTypes.DISTANCE]: "Distance (Cardio)",
  [ExercisePrescriptionTypes.INTERVAL]: "Intervals (HIIT)",
};

const formatExerciseFallbackName = (value) => {
  if (!value) return "No exercise selected";
  if (typeof value !== "string") return String(value);
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const AUTO_SAVE_DELAY_MS = 1500;

/** Wraps chips so tooltips work reliably inside flex rows (MUI Tooltip needs a non-disabled ref target). */
function StatusChipHint({ title, children }) {
  return (
    <Tooltip title={title} enterDelay={400}>
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

const getCoverageChipSx = (count, activeColor) =>
  count > 0
    ? (theme) => ({
        color: theme.palette[activeColor].main,
        borderColor: theme.palette[activeColor].main,
        backgroundColor: alpha(theme.palette[activeColor].main, 0.12),
        "& .MuiChip-label": { fontWeight: 600 },
      })
    : undefined;

const buildMetadataHash = ({ plan, initialPlanType, selectedClientUID }) => {
  if (!plan) return "";
  const effectivePlanType = initialPlanType === "template" ? "template" : plan.planType || "template";
  const effectiveClientUID =
    effectivePlanType === "client" ? selectedClientUID || plan.clientUID || null : null;
  return JSON.stringify({
    name: plan.name || "",
    description: plan.description || "",
    planType: effectivePlanType,
    clientUID: effectiveClientUID,
    privacy: plan.privacy || "private",
    difficulty: plan.difficulty || "beginner",
    type: plan.type || "general",
    protocolLabels: plan.protocolLabels || [],
    status: plan?.v2Meta?.status || plan?.status || "draft",
  });
};

const buildExercisesHash = (list = []) => {
  return JSON.stringify(
    (list || []).map((item) => ({
      id: item.id,
      index: item.index,
      type: item.type,
      exercise: item.exercise,
      superSetName: item.superSetName,
      sets: item.sets,
      setsToFailure: item.setsToFailure,
      reps: item.reps,
      tempo: item.tempo,
      rest: item.rest,
      notes: item.notes,
      labels: item.labels || [],
      prescriptionType: item.prescriptionType,
      durationSeconds: item.durationSeconds,
      durationValue: item.durationValue,
      distanceKilometers: item.distanceKilometers,
      distanceValue: item.distanceValue,
      intervalWorkSeconds: item.intervalWorkSeconds,
      intervalWorkValue: item.intervalWorkValue,
      intervalRestSeconds: item.intervalRestSeconds,
      intervalRestValue: item.intervalRestValue,
      intervalRounds: item.intervalRounds,
      exercises: item.exercises || [],
      alternatives: item.alternatives || [],
    })),
  );
};

// Deprecated `repsMax` field — no longer supported. Strip any legacy values loaded
// from storage so new saves never re-write the property.
const stripRepsMaxFromExercises = (list = []) =>
  (list || []).map((item) => {
    if (!item) return item;
    const copy = { ...item };
    delete copy.repsMax;
    if (Array.isArray(copy.exercises)) {
      copy.exercises = copy.exercises.map((child) => {
        if (!child) return child;
        const c = { ...child };
        delete c.repsMax;
        return c;
      });
    }
    if (Array.isArray(copy.alternatives)) {
      copy.alternatives = copy.alternatives.map((alt) => {
        if (!alt) return alt;
        const a = { ...alt };
        delete a.repsMax;
        return a;
      });
    }
    return copy;
  });

const BULK_EDIT_PRESETS = {
  none: {},
  hypertrophy_4x10: { sets: "4", reps: "10", rest: "60s" },
  strength_5x5: { sets: "5", reps: "5", rest: "120s" },
  pyramid_12_10_8_6: { sets: "4", reps: "12-10-8-6", rest: "90s" },
};

/** Shown when leaving the editor with unpublished staging (browser + in-app). */
export const TRAINING_PLAN_V2_STAGING_LEAVE_MESSAGE =
  "You have unpublished staged changes. Leave without publishing?";

function isTrainingPlanV2EditorPath(pathname) {
  return (
    /\/home\/training-plans-v2\/[^/]+\/edit/.test(pathname) ||
    /^\/home\/training-plans-v2\/new/.test(pathname)
  );
}

/** Browser Back leaves the editor before our popstate handler runs; go(1) restores URL and remounts. */
let tpV2StealthPopstateRestore = false;
let tpV2StealthPreserveSplitId = null;
let tpV2StealthPopstatePlanId = null;

function consumeTpV2StealthPopstateLoad(currentPlanId) {
  if (!tpV2StealthPopstateRestore) return null;
  if (!currentPlanId || tpV2StealthPopstatePlanId !== currentPlanId) {
    clearTpV2StealthPopstateRestore();
    return null;
  }
  tpV2StealthPopstateRestore = false;
  const preserveSplitId = tpV2StealthPreserveSplitId;
  tpV2StealthPreserveSplitId = null;
  tpV2StealthPopstatePlanId = null;
  return { silent: true, stealth: true, preserveSplitId };
}

function clearTpV2StealthPopstateRestore() {
  tpV2StealthPopstateRestore = false;
  tpV2StealthPreserveSplitId = null;
  tpV2StealthPopstatePlanId = null;
}

const TrainingPlansV2Editor = ({ onStagingNavGuardChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  /** New-plan bootstrap only; do not use full `searchParams` in loadData deps (guided `gt`/`gs` would reset the form). */
  const planTypeQueryParam = searchParams.get("type");
  const isCreate = !planId;

  const lastTpEditorPathRef = React.useRef(`${location.pathname}${location.search}`);
  const allowNextPopstateExitRef = React.useRef(false);
  const popstateConfirmInFlightRef = React.useRef(false);

  React.useEffect(() => {
    const p = `${location.pathname}${location.search}`;
    if (isTrainingPlanV2EditorPath(location.pathname)) {
      lastTpEditorPathRef.current = p;
    }
  }, [location.pathname, location.search]);

  const [plan, setPlan] = React.useState(null);
  const [loading, setLoading] = React.useState(() => !tpV2StealthPopstateRestore);
  const [selectedSplitId, setSelectedSplitId] = React.useState(null);
  const [splits, setSplits] = React.useState([]);
  const [exercises, setExercises] = React.useState([]);
  const [activeTab, setActiveTab] = React.useState("details");
  const [message, setMessageText] = React.useState("");
  const [messageSeverity, setMessageSeverity] = React.useState("info");
  const [planStructureBlockNotice, setPlanStructureBlockNotice] = React.useState("");
  const setMessage = React.useCallback((text, severity = "info") => {
    const t = typeof text === "string" ? text : "";
    setMessageText(t);
    setMessageSeverity(t ? severity : "info");
  }, []);
  const [selectedClientUID, setSelectedClientUID] = React.useState("");
  const [clients, setClients] = React.useState([]);
  const [assignmentCoverage, setAssignmentCoverage] = React.useState({
    primaryCount: 0,
    alternativeCount: 0,
  });
  const [assignmentCoverageReady, setAssignmentCoverageReady] = React.useState(false);
  const [hasStagingDoc, setHasStagingDoc] = React.useState(false);
  const [showInactiveClients, setShowInactiveClients] = React.useState(false);
  const [initialPlanType, setInitialPlanType] = React.useState(null);
  const [exerciseCatalog, setExerciseCatalog] = React.useState([]);
  const [showAdvancedByExercise, setShowAdvancedByExercise] = React.useState({});
  const [showAlternativesByExercise, setShowAlternativesByExercise] = React.useState({});
  const [splitNameDraft, setSplitNameDraft] = React.useState("");
  const [splitDescriptionDraft, setSplitDescriptionDraft] = React.useState("");
  const [splitVariantPathDraft, setSplitVariantPathDraft] = React.useState(false);
  const [splitLabelsDraft, setSplitLabelsDraft] = React.useState([]);
  const [hasUnsavedMetadata, setHasUnsavedMetadata] = React.useState(false);
  const [hasUnsavedExercises, setHasUnsavedExercises] = React.useState(false);
  const [isAutoSaving, setIsAutoSaving] = React.useState(false);
  const [lastSavedAt, setLastSavedAt] = React.useState(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = React.useState(false);
  const [isGeneratingWorkoutDescription, setIsGeneratingWorkoutDescription] = React.useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = React.useState(false);
  const appendImportInputRef = React.useRef(null);
  const [appendImporting, setAppendImporting] = React.useState(false);
  const [appendImportError, setAppendImportError] = React.useState("");
  const [importSource, setImportSource] = React.useState(loadStoredTrainingPlanImportSource);
  const [pendingScrollExerciseId, setPendingScrollExerciseId] = React.useState(null);
  const [showBulkAddDialog, setShowBulkAddDialog] = React.useState(false);
  const [showCopyWorkoutDialog, setShowCopyWorkoutDialog] = React.useState(false);
  const [bulkAddDraftExerciseId, setBulkAddDraftExerciseId] = React.useState(null);
  const [bulkAddQueue, setBulkAddQueue] = React.useState([]);
  const [selectedExerciseIds, setSelectedExerciseIds] = React.useState([]);
  const [showBulkEditDialog, setShowBulkEditDialog] = React.useState(false);
  const [bulkEditDraft, setBulkEditDraft] = React.useState({
    sets: "",
    reps: "",
    rest: "",
    tempo: "",
    notes: "",
  });
  const [bulkEditPreset, setBulkEditPreset] = React.useState("none");
  const [overflowMenuAnchor, setOverflowMenuAnchor] = React.useState(null);
  const [isSavingAsDraftVersion, setIsSavingAsDraftVersion] = React.useState(false);
  const [isReverting, setIsReverting] = React.useState(false);
  const [isPublishing, setIsPublishing] = React.useState(false);
  const hints = useTrainingPlansV2Hints("editor");
  const { hasFeature, organizationData } = useEntitlements();
  const canUseTrainingPlanAi =
    hasFeature("training_plan_data_entry_assistance") ||
    hasFeature("ai_features");
  const canImportPlans = hasFeature("training_plan_imports");
  const savedMetadataHashRef = React.useRef("");
  const savedExercisesHashRef = React.useRef("");
  const exercisesBySplitIdRef = React.useRef({});
  const exerciseRowRefs = React.useRef({});

  const useEditorStaging = React.useMemo(() => {
    if (isCreate || !planId || !plan || !assignmentCoverageReady) {
      return false;
    }
    const st = normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft");
    if (st !== "active") {
      return false;
    }
    return (
      (assignmentCoverage.primaryCount || 0) > 0 || (assignmentCoverage.alternativeCount || 0) > 0
    );
  }, [assignmentCoverage, assignmentCoverageReady, isCreate, plan, planId]);
  const { data: exercisesBigQueryData } = useExercisesBigQuery({
    limit: 1000,
    enabled: true,
  });

  const { mutateAsync: createCustomExerciseMutation } = useCreateCustomExerciseBigQuery();

  const handleCreateCustomExercise = React.useCallback(
    async (rawName) => {
      const name = (rawName || "").trim();
      if (!name) {
        throw new Error("Exercise name is required");
      }
      const exerciseId = nanoid();
      await createCustomExerciseMutation({
        exerciseId,
        exerciseData: {
          id: exerciseId,
          name,
          description: `Custom exercise: ${name}`,
          difficulty: "beginner",
          instructions: "",
          icon: "",
          isCustom: true,
          mainMuscleGroups: [],
          secondaryMuscleGroups: [],
          equipment: [],
        },
      });
      return exerciseId;
    },
    [createCustomExerciseMutation],
  );

  const exerciseOptions = React.useMemo(() => {
    const bigQueryExercises =
      exercisesBigQueryData?.data?.exercises?.map((exercise) => ({
        id: exercise.id || exercise.exerciseId || exercise.exercise_id,
        name:
          exercise.name ||
          exercise.exerciseName ||
          exercise.exercise_name ||
          exercise.title ||
          exercise.id,
        type: exercise.type || exercise.exerciseType || "",
        isCustom: !!exercise.isCustom,
      })) || [];
    const inlineFromPlan = exercises.flatMap((ex) => {
      const rows = [];
      if (ex.type === WorkoutTypes.superSet && Array.isArray(ex.exercises)) {
        ex.exercises.forEach((child) => {
          if (child?.exercise && child?.exerciseName) {
            rows.push({
              id: child.exercise,
              name: child.exerciseName,
              type: "",
              isCustom: false,
            });
          }
        });
      }
      if (ex.exercise && ex.exerciseName) {
        rows.push({ id: ex.exercise, name: ex.exerciseName, type: "", isCustom: false });
      }
      return rows;
    });
    const merged = [...bigQueryExercises, ...exerciseCatalog, ...inlineFromPlan];
    const unique = Array.from(
      new Map(merged.filter((e) => !!e?.id).map((e) => [e.id, e])).values(),
    );
    return unique.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [exerciseCatalog, exercisesBigQueryData, exercises]);

  const getExerciseNameById = React.useCallback(
    (exerciseId, fallbackName) => {
      if (!exerciseId) return "No exercise selected";
      const matchById = exerciseOptions.find((exercise) => exercise.id === exerciseId);
      if (matchById?.name) return matchById.name;
      const matchByName = exerciseOptions.find((exercise) => exercise.name === exerciseId);
      if (matchByName?.name) return matchByName.name;
      if (fallbackName) return fallbackName;
      return formatExerciseFallbackName(exerciseId);
    },
    [exerciseOptions],
  );

  const loadData = React.useCallback(async (options = {}) => {
    const {
      silent = false,
      stealth = false,
      preserveSplitId = null,
      reloadFromLiveOnly = false,
    } = options;
    if (!planId) {
      const typeFromQuery = planTypeQueryParam === "client" ? "client" : "template";
      setInitialPlanType(typeFromQuery);
      setSelectedClientUID("");
      const nextPlan = {
        name: "",
        description: "",
        planType: typeFromQuery,
        privacy: "private",
        difficulty: "beginner",
        type: "general",
        status: "draft",
        protocolLabels: [],
      };
      setPlan(nextPlan);
      savedMetadataHashRef.current = buildMetadataHash({
        plan: nextPlan,
        initialPlanType: typeFromQuery,
        selectedClientUID: "",
      });
      savedExercisesHashRef.current = buildExercisesHash([]);
      exercisesBySplitIdRef.current = {};
      setHasUnsavedMetadata(false);
      setHasUnsavedExercises(false);
      setHasStagingDoc(false);
      setAssignmentCoverageReady(true);
      setLoading(false);
      return;
    }

    let loadingWasSet = false;
    if (!silent) {
      setLoading(true);
      loadingWasSet = true;
    }
    if (!stealth) {
      setAssignmentCoverageReady(false);
    }
    try {
      const existing = await getPlanById(planId);
      const splitData = await getSplits(planId);
      const coverageResult = await getPlanAssignmentCoverage({ planIds: [planId] });
      const cov = coverageResult?.[planId] || { primaryCount: 0, alternativeCount: 0 };
      setAssignmentCoverage(cov);
      setAssignmentCoverageReady(true);

      const shouldUseStaging =
        normalizePlanLifecycleStatus(existing?.v2Meta?.status || existing?.status || "draft") ===
          "active" &&
        ((cov.primaryCount || 0) > 0 || (cov.alternativeCount || 0) > 0);

      let workingPlan = existing;
      let workingSplits = splitData;

      const populateRefFromLive = async () => {
        exercisesBySplitIdRef.current = {};
        for (const s of splitData) {
          if (!s.isRestSplit) {
            exercisesBySplitIdRef.current[s.id] = await getSplitExercises(s.id);
          }
        }
      };

      if (reloadFromLiveOnly) {
        workingPlan = existing;
        workingSplits = splitData;
        await populateRefFromLive();
        setHasStagingDoc(false);
      } else if (shouldUseStaging) {
        const staging = await getEditorStaging(planId);
        if (staging?.plan && Array.isArray(staging.splits)) {
          workingPlan = staging.plan;
          workingSplits = staging.splits;
          exercisesBySplitIdRef.current = JSON.parse(
            JSON.stringify(staging.exercisesBySplitId || {}),
          );
          setHasStagingDoc(true);
        } else {
          workingPlan = existing;
          workingSplits = splitData;
          await populateRefFromLive();
          setHasStagingDoc(false);
        }
      } else {
        workingPlan = existing;
        workingSplits = splitData;
        exercisesBySplitIdRef.current = {};
        setHasStagingDoc(false);
      }

      const nextInitialPlanType = workingPlan?.planType || "template";
      const nextClientUID = workingPlan?.clientUID || "";
      setInitialPlanType(nextInitialPlanType);
      setSelectedClientUID(nextClientUID);
      setPlan(workingPlan);
      setSplits(workingSplits);
      savedMetadataHashRef.current = buildMetadataHash({
        plan: workingPlan,
        initialPlanType: nextInitialPlanType,
        selectedClientUID: nextClientUID,
      });
      setHasUnsavedMetadata(false);

      const preferredSplitId =
        preserveSplitId && workingSplits.some((s) => s.id === preserveSplitId)
          ? preserveSplitId
          : workingSplits[0]?.id ?? null;

      if (preferredSplitId) {
        const sel = workingSplits.find((s) => s.id === preferredSplitId);
        setSelectedSplitId(preferredSplitId);
        if (sel?.isRestSplit) {
          setExercises([]);
          savedExercisesHashRef.current = buildExercisesHash([]);
        } else if (shouldUseStaging && !reloadFromLiveOnly) {
          const cached = exercisesBySplitIdRef.current[preferredSplitId];
          const splitExercises = Array.isArray(cached)
            ? cached
            : await getSplitExercises(preferredSplitId);
          const normalizedExercises = stripRepsMaxFromExercises(splitExercises);
          setExercises(normalizedExercises);
          savedExercisesHashRef.current = buildExercisesHash(normalizedExercises);
        } else {
          const splitExercises = await getSplitExercises(preferredSplitId);
          const normalizedExercises = stripRepsMaxFromExercises(splitExercises);
          setExercises(normalizedExercises);
          savedExercisesHashRef.current = buildExercisesHash(normalizedExercises);
        }
      } else {
        setSelectedSplitId(null);
        setExercises([]);
        savedExercisesHashRef.current = buildExercisesHash([]);
      }
      setHasUnsavedExercises(false);
    } catch (error) {
      setMessage(error?.message || "Unable to load plan.", "error");
      throw error;
    } finally {
      if (loadingWasSet || silent) {
        setLoading(false);
      }
    }
  }, [planId, planTypeQueryParam, setMessage]);

  const selectedSplitIdRef = React.useRef(selectedSplitId);
  selectedSplitIdRef.current = selectedSplitId;
  const planIdRef = React.useRef(planId);
  planIdRef.current = planId;

  React.useEffect(() => {
    if (planId) {
      setPlanStructureBlockNotice("");
    }
  }, [planId]);

  React.useEffect(() => {
    const stealthOpts = consumeTpV2StealthPopstateLoad(planId);
    if (stealthOpts) {
      loadData(stealthOpts);
      return;
    }
    loadData();
  }, [loadData, planId]);

  const handleAppendImportFileChange = React.useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file || !planId) {
        return;
      }
      setAppendImportError("");
      setAppendImporting(true);
      try {
        const formData = new FormData();
        formData.append("file", file, file.name || "training-plan.pdf");
        formData.append("appendToProtocolId", planId);
        formData.append("importSource", normalizeTrainingPlanImportSource(importSource));
        const effectiveClient =
          (plan?.planType || initialPlanType) === "client"
            ? selectedClientUID || plan?.clientUID || ""
            : "";
        if (effectiveClient) {
          formData.append("clientUID", effectiveClient);
        }
        const result = await importTrainingPlan(formData);
        await trackTrainingPlansV2Event("plan_ai_import_append", {
          planId,
          splitsAdded: result?.stats?.splitsCreated ?? 0,
          exercisesAdded: result?.stats?.exercisesCreated ?? 0,
        });
        setMessage(
          `Imported ${result?.stats?.splitsCreated ?? 0} workout(s) with ${result?.stats?.exercisesCreated ?? 0} exercise row(s).`,
        );
        await loadData();
      } catch (err) {
        setAppendImportError(err?.message || "Import failed. Please try again.");
      } finally {
        setAppendImporting(false);
        e.target.value = "";
      }
    },
    [
      importSource,
      initialPlanType,
      loadData,
      plan?.clientUID,
      plan?.planType,
      planId,
      selectedClientUID,
      setMessage,
    ],
  );

  React.useEffect(() => {
    async function loadExerciseCatalog() {
      setExerciseCatalog(await getExerciseCatalog());
    }
    loadExerciseCatalog();
  }, []);

  React.useEffect(() => {
    async function loadClients() {
      const value = await getAssignableClients();
      setClients(value || []);
    }
    loadClients();
  }, []);

  const selectedSplit = React.useMemo(
    () => splits.find((split) => split.id === selectedSplitId) || null,
    [selectedSplitId, splits],
  );

  const flushExercisesToRef = React.useCallback(() => {
    if (!planId || !selectedSplitId || !selectedSplit) {
      return;
    }
    if (selectedSplit.isRestSplit) {
      exercisesBySplitIdRef.current[selectedSplitId] = [];
      return;
    }
    exercisesBySplitIdRef.current[selectedSplitId] = exercises;
  }, [exercises, planId, selectedSplit, selectedSplitId]);

  const buildStagingSnapshot = React.useCallback(() => {
    flushExercisesToRef();
    return {
      plan,
      splits,
      exercisesBySplitId: { ...exercisesBySplitIdRef.current },
    };
  }, [flushExercisesToRef, plan, splits]);

  const remapExercisesForSplitCopy = React.useCallback((rows, splitId) => {
    return stripRepsMaxFromExercises(rows || []).map((ex, idx) => ({
      ...ex,
      id: nanoid(),
      protocolId: planId,
      splitId,
      index: idx,
      exercises: (ex.exercises || []).map((child) => ({ ...child, id: nanoid() })),
    }));
  }, [planId]);

  React.useEffect(() => {
    setSplitNameDraft(selectedSplit?.name || "");
    setSplitDescriptionDraft(selectedSplit?.description || "");
    setSplitVariantPathDraft(selectedSplit?.isVariantPath ?? false);
    setSplitLabelsDraft(Array.isArray(selectedSplit?.labels) ? [...selectedSplit.labels] : []);
  }, [selectedSplit?.description, selectedSplit?.id, selectedSplit?.name, selectedSplit?.isVariantPath, selectedSplit?.labels]);

  const splitDraftDirty = React.useMemo(() => {
    if (!selectedSplit) return false;
    const draftName = (splitNameDraft || "").trim();
    const persistedName = (selectedSplit.name || "").trim();
    const draftDesc = splitDescriptionDraft ?? "";
    const persistedDesc = selectedSplit.description ?? "";
    const draftVariant = splitVariantPathDraft ?? false;
    const persistedVariant = selectedSplit.isVariantPath ?? false;
    const draftLabels = Array.isArray(splitLabelsDraft) ? splitLabelsDraft : [];
    const persistedLabels = Array.isArray(selectedSplit.labels) ? selectedSplit.labels : [];
    const labelsChanged = draftLabels.length !== persistedLabels.length || draftLabels.some((id, idx) => id !== persistedLabels[idx]);
    return draftName !== persistedName || draftDesc !== persistedDesc || draftVariant !== persistedVariant || labelsChanged;
  }, [selectedSplit, splitDescriptionDraft, splitNameDraft, splitVariantPathDraft, splitLabelsDraft]);

  const hasPersistableSplitDraft = React.useMemo(() => {
    if (!planId || !selectedSplit) return false;
    const draftName = (splitNameDraft || "").trim();
    if (!draftName) return false;
    const persistedName = (selectedSplit.name || "").trim();
    const draftDesc = splitDescriptionDraft ?? "";
    const persistedDesc = selectedSplit.description ?? "";
    const draftVariant = splitVariantPathDraft ?? false;
    const persistedVariant = selectedSplit.isVariantPath ?? false;
    const draftLabels = Array.isArray(splitLabelsDraft) ? splitLabelsDraft : [];
    const persistedLabels = Array.isArray(selectedSplit.labels) ? selectedSplit.labels : [];
    const labelsChanged = draftLabels.length !== persistedLabels.length || draftLabels.some((id, idx) => id !== persistedLabels[idx]);
    return draftName !== persistedName || draftDesc !== persistedDesc || draftVariant !== persistedVariant || labelsChanged;
  }, [planId, selectedSplit, splitDescriptionDraft, splitNameDraft, splitVariantPathDraft, splitLabelsDraft]);

  const warnStagingNavigateAway = React.useMemo(
    () =>
      useEditorStaging &&
      (hasStagingDoc ||
        hasUnsavedMetadata ||
        hasUnsavedExercises ||
        splitDraftDirty),
    [
      useEditorStaging,
      hasStagingDoc,
      hasUnsavedMetadata,
      hasUnsavedExercises,
      splitDraftDirty,
    ],
  );

  React.useEffect(() => {
    onStagingNavGuardChange?.(warnStagingNavigateAway);
  }, [onStagingNavGuardChange, warnStagingNavigateAway]);

  React.useEffect(() => {
    return () => onStagingNavGuardChange?.(false);
  }, [onStagingNavGuardChange]);

  useBeforeUnload(
    React.useCallback(
      (event) => {
        if (!warnStagingNavigateAway) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [warnStagingNavigateAway],
    ),
  );

  React.useEffect(() => {
    if (!warnStagingNavigateAway) return undefined;
    const handleCaptureClick = (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      const link = el.closest("a[href]");
      if (!link) return;
      if (link.target === "_blank" || link.hasAttribute("download")) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const here = `${window.location.pathname}${window.location.search}`;
      const there = `${url.pathname}${url.search}`;
      if (there === here) return;
      if (!isTrainingPlanV2EditorPath(window.location.pathname)) return;
      if (!window.confirm(TRAINING_PLAN_V2_STAGING_LEAVE_MESSAGE)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("click", handleCaptureClick, true);
    return () => document.removeEventListener("click", handleCaptureClick, true);
  }, [warnStagingNavigateAway]);

  React.useEffect(() => {
    if (!warnStagingNavigateAway) return undefined;

    const handlePopState = () => {
      if (allowNextPopstateExitRef.current) {
        allowNextPopstateExitRef.current = false;
        return;
      }
      if (popstateConfirmInFlightRef.current) {
        return;
      }

      if (isTrainingPlanV2EditorPath(window.location.pathname)) {
        return;
      }

      tpV2StealthPopstateRestore = true;
      tpV2StealthPreserveSplitId = selectedSplitIdRef.current;
      tpV2StealthPopstatePlanId = planIdRef.current;

      popstateConfirmInFlightRef.current = true;
      window.history.go(1);

      window.setTimeout(() => {
        popstateConfirmInFlightRef.current = false;

        const onEditorNow = isTrainingPlanV2EditorPath(window.location.pathname);
        if (!onEditorNow && lastTpEditorPathRef.current) {
          navigate(lastTpEditorPathRef.current, { replace: true });
        }

        const ok = window.confirm(TRAINING_PLAN_V2_STAGING_LEAVE_MESSAGE);
        if (!ok) {
          return;
        }

        clearTpV2StealthPopstateRestore();
        allowNextPopstateExitRef.current = true;
        window.history.back();
      }, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [warnStagingNavigateAway, navigate]);

  const metadataHash = React.useMemo(
    () => buildMetadataHash({ plan, initialPlanType, selectedClientUID }),
    [initialPlanType, plan, selectedClientUID],
  );
  const exercisesHash = React.useMemo(() => buildExercisesHash(exercises), [exercises]);

  React.useEffect(() => {
    if (!plan) {
      setHasUnsavedMetadata(false);
      return;
    }
    setHasUnsavedMetadata(metadataHash !== savedMetadataHashRef.current);
  }, [metadataHash, plan]);

  React.useEffect(() => {
    if (!selectedSplitId) {
      setHasUnsavedExercises(false);
      return;
    }
    setHasUnsavedExercises(exercisesHash !== savedExercisesHashRef.current);
  }, [exercisesHash, selectedSplitId]);

  React.useEffect(() => {
    if (!pendingScrollExerciseId) return;
    const rowElement = exerciseRowRefs.current[pendingScrollExerciseId];
    if (!rowElement) return;
    rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
    setPendingScrollExerciseId(null);
  }, [exercises, pendingScrollExerciseId]);

  React.useEffect(() => {
    const exerciseIdSet = new Set(exercises.map((exercise) => exercise.id));
    setSelectedExerciseIds((prev) => prev.filter((id) => exerciseIdSet.has(id)));
  }, [exercises]);

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (activeTab !== "exercises") return;
      const target = event.target;
      const isTypingTarget =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.getAttribute("contenteditable") === "true");
      if (isTypingTarget) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "d") {
        event.preventDefault();
        if (selectedExerciseIds.length === 1) {
          handleDuplicateExercise(selectedExerciseIds[0]);
        }
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
        event.preventDefault();
        if (selectedSplit?.isRestSplit) {
          return;
        }
        setShowBulkAddDialog(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeTab, selectedExerciseIds, selectedSplit?.isRestSplit]);

  const effectivePlanTypeForSave = initialPlanType === "template" ? "template" : plan?.planType || "template";
  const effectiveClientUIDForSave =
    effectivePlanTypeForSave === "client" ? selectedClientUID || plan?.clientUID || null : null;
  const isClientPlanMissingClientForSave =
    effectivePlanTypeForSave === "client" && !effectiveClientUIDForSave;

  const persistMetadataDraft = React.useCallback(async () => {
    if (!planId || !plan) return false;
    if (!plan?.name?.trim()) return false;
    if (isClientPlanMissingClientForSave) return false;
    if (useEditorStaging) {
      flushExercisesToRef();
      await saveEditorStaging(planId, buildStagingSnapshot());
      setHasStagingDoc(true);
      savedMetadataHashRef.current = buildMetadataHash({
        plan: {
          ...plan,
          planType: effectivePlanTypeForSave,
          clientUID: effectiveClientUIDForSave,
        },
        initialPlanType,
        selectedClientUID,
      });
      setHasUnsavedMetadata(false);
      return true;
    }
    // Do not force status to "draft" here — active assigned plans must stay active
    // (savePlanMetadata rejects demoting an assigned active plan). Preserve v2Meta.status from the loaded plan.
    await savePlanMetadata(planId, {
      ...plan,
      planType: effectivePlanTypeForSave,
      clientUID: effectiveClientUIDForSave,
      v2Meta: { ...(plan.v2Meta || {}), lastEditedByEditor: "v2" },
    });
    savedMetadataHashRef.current = buildMetadataHash({
      plan: {
        ...plan,
        planType: effectivePlanTypeForSave,
        clientUID: effectiveClientUIDForSave,
      },
      initialPlanType,
      selectedClientUID,
    });
    setHasUnsavedMetadata(false);
    return true;
  }, [
    buildStagingSnapshot,
    effectiveClientUIDForSave,
    effectivePlanTypeForSave,
    flushExercisesToRef,
    initialPlanType,
    isClientPlanMissingClientForSave,
    plan,
    planId,
    selectedClientUID,
    useEditorStaging,
  ]);

  const persistCurrentSplitExercises = React.useCallback(async () => {
    if (!planId || !selectedSplitId || !exercises.length) return false;
    if (selectedSplit?.isRestSplit) return false;
    if (useEditorStaging) {
      flushExercisesToRef();
      await saveEditorStaging(planId, buildStagingSnapshot());
      setHasStagingDoc(true);
      savedExercisesHashRef.current = buildExercisesHash(exercises);
      setHasUnsavedExercises(false);
      return true;
    }
    await Promise.all(
      exercises.map((exercise, index) =>
        upsertExercise({
          ...exercise,
          protocolId: planId,
          splitId: selectedSplitId,
          index: exercise?.index ?? index,
        }),
      ),
    );
    savedExercisesHashRef.current = buildExercisesHash(exercises);
    setHasUnsavedExercises(false);
    return true;
  }, [
    buildStagingSnapshot,
    exercises,
    flushExercisesToRef,
    planId,
    selectedSplit,
    selectedSplitId,
    useEditorStaging,
  ]);

  const persistCurrentSplitDraft = React.useCallback(async () => {
    if (!planId || !selectedSplit) return false;
    const nextName = (splitNameDraft || "").trim();
    if (!nextName) return false;
    const nextDesc = splitDescriptionDraft ?? "";
    const nextVariant = splitVariantPathDraft ?? false;
    const nextLabels = Array.isArray(splitLabelsDraft) ? splitLabelsDraft : [];
    const nameChanged = nextName !== (selectedSplit.name || "").trim();
    const descChanged = nextDesc !== (selectedSplit.description ?? "");
    const variantChanged = nextVariant !== (selectedSplit.isVariantPath ?? false);
    const persistedLabels = Array.isArray(selectedSplit.labels) ? selectedSplit.labels : [];
    const labelsChanged = nextLabels.length !== persistedLabels.length || nextLabels.some((id, idx) => id !== persistedLabels[idx]);
    if (!nameChanged && !descChanged && !variantChanged && !labelsChanged) return false;

    const nextSplits = splits.map((split) =>
      split.id === selectedSplit.id
        ? { ...split, name: nextName, description: nextDesc, isVariantPath: nextVariant, labels: nextLabels }
        : split,
    );
    setSplits(nextSplits);

    if (useEditorStaging) {
      flushExercisesToRef();
      await saveEditorStaging(planId, {
        plan,
        splits: nextSplits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      return true;
    }

    await upsertSplit(
      {
        ...selectedSplit,
        protocolId: planId,
        name: nextName,
        description: nextDesc,
        isVariantPath: nextVariant,
        labels: nextLabels,
      },
      false,
    );

    await appendRevision(planId, {
      action: "update_workout_details",
      summary: `Updated details for ${nextName}`,
    });
    return true;
  }, [
    flushExercisesToRef,
    planId,
    selectedSplit,
    splitDescriptionDraft,
    splitNameDraft,
    splitVariantPathDraft,
    splitLabelsDraft,
    plan,
    splits,
    useEditorStaging,
  ]);

  const persistWorkoutLabels = React.useCallback(
    async (nextLabelIdsRaw) => {
      if (!planId || !selectedSplit) return;

      const normalized = Array.isArray(nextLabelIdsRaw) ? [...nextLabelIdsRaw].filter(Boolean) : [];
      const prev = Array.isArray(selectedSplit.labels) ? selectedSplit.labels : [];
      if (
        prev.length === normalized.length && prev.every((id, idx) => id === normalized[idx])
      ) {
        return;
      }

      const nextSplits = splits.map((split) =>
        split.id === selectedSplit.id ? { ...split, labels: normalized } : split,
      );
      setSplits(nextSplits);

      if (useEditorStaging) {
        flushExercisesToRef();
        await saveEditorStaging(planId, {
          plan,
          splits: nextSplits,
          exercisesBySplitId: { ...exercisesBySplitIdRef.current },
        });
        setHasStagingDoc(true);
        setLastSavedAt(new Date());
        return;
      }

      await upsertSplit(
        {
          ...selectedSplit,
          protocolId: planId,
          labels: normalized,
        },
        false,
      );

      await appendRevision(planId, {
        action: "update_workout_labels",
        summary: `Updated labels for ${selectedSplit.name || "workout"}`,
      });
      setLastSavedAt(new Date());
    },
    [
      flushExercisesToRef,
      plan,
      planId,
      saveEditorStaging,
      selectedSplit,
      splits,
      useEditorStaging,
    ],
  );
  const flushSplitDraftOnBlur = React.useCallback(async () => {
    if (!hasPersistableSplitDraft) return;
    try {
      setIsAutoSaving(true);
      const saved = await persistCurrentSplitDraft();
      if (saved) {
        setLastSavedAt(new Date());
      }
    } catch (error) {
      setMessage(error?.message || "Failed to save workout details.", "error");
    } finally {
      setIsAutoSaving(false);
    }
  }, [hasPersistableSplitDraft, persistCurrentSplitDraft]);

  React.useEffect(() => {
    if (!planId || isAutoSaving || isReverting || isPublishing) return undefined;
    const blockAutoSaveUntilCoverage =
      !isCreate &&
      plan &&
      normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft") === "active" &&
      !assignmentCoverageReady;
    if (blockAutoSaveUntilCoverage) return undefined;
    if (!hasUnsavedMetadata && !hasUnsavedExercises && !hasPersistableSplitDraft) return undefined;
    if (!plan?.name?.trim() || isClientPlanMissingClientForSave) return undefined;

    const timeoutId = setTimeout(async () => {
      try {
        setIsAutoSaving(true);
        let savedAnything = false;
        if (hasUnsavedMetadata) {
          savedAnything = (await persistMetadataDraft()) || savedAnything;
        }
        if (hasPersistableSplitDraft) {
          savedAnything = (await persistCurrentSplitDraft()) || savedAnything;
        }
        if (hasUnsavedExercises) {
          savedAnything = (await persistCurrentSplitExercises()) || savedAnything;
        }
        if (savedAnything) {
          setLastSavedAt(new Date());
        }
      } catch (error) {
        setMessage(error?.message || "Auto-save failed. Use Save changes to retry.", "error");
      } finally {
        setIsAutoSaving(false);
      }
    }, AUTO_SAVE_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [
    hasPersistableSplitDraft,
    hasUnsavedExercises,
    hasUnsavedMetadata,
    isAutoSaving,
    isClientPlanMissingClientForSave,
    persistCurrentSplitDraft,
    persistCurrentSplitExercises,
    persistMetadataDraft,
    plan?.name,
    planId,
    isReverting,
    isPublishing,
    assignmentCoverageReady,
    isCreate,
    plan,
  ]);

  const refreshExercises = async (splitId) => {
    if (planId && selectedSplitId && selectedSplitId !== splitId) {
      if (splitDraftDirty && !hasPersistableSplitDraft) {
        setMessage("Enter a workout name before switching workouts.", "error");
        return;
      }
      try {
        await persistCurrentSplitDraft();
      } catch (error) {
        setMessage(error?.message || "Failed to save workout details before switching.", "error");
        return;
      }
    }
    if (useEditorStaging) {
      if (planId && selectedSplitId && selectedSplit && !selectedSplit.isRestSplit) {
        exercisesBySplitIdRef.current[selectedSplitId] = exercises;
      }
      setSelectedSplitId(splitId);
      const split = splits.find((s) => s.id === splitId);
      if (split?.isRestSplit) {
        setExercises([]);
        savedExercisesHashRef.current = buildExercisesHash([]);
        setHasUnsavedExercises(false);
        return;
      }
      const cached = exercisesBySplitIdRef.current[splitId];
      const next = Array.isArray(cached) ? [...cached] : [];
      setExercises(next);
      savedExercisesHashRef.current = buildExercisesHash(next);
      setHasUnsavedExercises(false);
      return;
    }
    setSelectedSplitId(splitId);
    const split = splits.find((s) => s.id === splitId);
    if (split?.isRestSplit) {
      setExercises([]);
      savedExercisesHashRef.current = buildExercisesHash([]);
      setHasUnsavedExercises(false);
      return;
    }
    const splitExercises = await getSplitExercises(splitId);
    const normalizedExercises = stripRepsMaxFromExercises(splitExercises);
    setExercises(normalizedExercises);
    savedExercisesHashRef.current = buildExercisesHash(normalizedExercises);
    setHasUnsavedExercises(false);
  };

  const getAiResponseContent = (response) => {
    const value =
      response?.response?.[0]?.message?.content ||
      response?.response?.message?.content ||
      response?.message?.content ||
      response?.content ||
      "";
    const raw = typeof value === "string" ? value : String(value || "");
    const trimmed = raw.trim();
    if (!trimmed) return "";

    const tryParseDescription = (input) => {
      try {
        const parsed = JSON.parse(input);
        if (parsed && typeof parsed === "object") {
          if (typeof parsed.description === "string") return parsed.description.trim();
          if (typeof parsed.summary === "string") return parsed.summary.trim();
        }
      } catch (error) {
        // Non-JSON response is expected in many cases.
      }
      return null;
    };

    const parsedDirect = tryParseDescription(trimmed);
    if (parsedDirect) return parsedDirect;

    const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    if (fencedMatch?.[1]) {
      const parsedFenced = tryParseDescription(fencedMatch[1].trim());
      if (parsedFenced) return parsedFenced;
    }

    return trimmed;
  };

  const formatExerciseSummaryForPrompt = React.useCallback(
    (exercise) => {
      const typeLabel = exercise.type === WorkoutTypes.superSet ? "superset" : "exercise";
      const exerciseName =
        exercise.type === WorkoutTypes.superSet
          ? exercise.superSetName || "Superset"
          : getExerciseNameById(exercise.exercise, exercise.exerciseName);
      const repValue = exercise.reps ?? "";
      const details = [
        exercise.sets ? `${exercise.sets} sets` : null,
        repValue ? `reps ${repValue}` : null,
        exercise.rest ? `rest ${exercise.rest}` : null,
      ].filter(Boolean);

      return `${exerciseName} (${typeLabel}${details.length ? `, ${details.join(", ")}` : ""})`;
    },
    [getExerciseNameById],
  );

  const handleGenerateDescription = async () => {
    if (!splits.length) {
      setMessage("Add at least one workout before generating a description.", "error");
      return;
    }

    setIsGeneratingDescription(true);
    try {
      if (useEditorStaging && planId && selectedSplitId && selectedSplit && !selectedSplit.isRestSplit) {
        exercisesBySplitIdRef.current[selectedSplitId] = exercises;
      }
      const workoutSummaries = await Promise.all(
        splits.map(async (split) => {
          let splitExercises;
          if (split.id === selectedSplitId) {
            splitExercises = exercises;
          } else if (useEditorStaging) {
            const cached = exercisesBySplitIdRef.current[split.id];
            splitExercises = Array.isArray(cached) ? cached : [];
          } else {
            splitExercises = await getSplitExercises(split.id);
          }
          const exerciseSummaries = (splitExercises || [])
            .slice(0, 10)
            .map((exercise) => formatExerciseSummaryForPrompt(exercise));
          return {
            workoutName: split.name,
            workoutType: split.isRestSplit ? "rest_day" : "workout",
            exerciseCount: splitExercises?.length || 0,
            exercises: exerciseSummaries,
          };
        }),
      );

      const promptPayload = {
        planName: plan?.name || "Untitled Training Plan",
        planType: plan?.type || "general",
        difficulty: plan?.difficulty || "beginner",
        workoutCount: workoutSummaries.length,
        workouts: workoutSummaries,
      };

      const aiResponse = await getChatGPTResponse({
        temperature: 0.3,
        messages: [
          {
            role: "system",
            content:
              "You are a fitness programming assistant. Write concise coach-facing plan summaries.",
          },
          {
            role: "user",
            content:
              "Create a concise training plan description in 2-4 sentences. Mention split structure, training focus, and progression intent. Avoid markdown bullets, avoid hype, keep practical tone.",
          },
          {
            role: "user",
            content: `Plan data JSON:\n${JSON.stringify(promptPayload, null, 2)}`,
          },
        ],
      });

      const generatedDescription = getAiResponseContent(aiResponse).trim();
      if (!generatedDescription) {
        throw new Error("AI returned an empty description.");
      }

      setPlan((prev) => ({ ...prev, description: generatedDescription }));
      setMessage("AI description generated. Review and edit if needed.");
    } catch (error) {
      setMessage(error?.message || "Unable to generate description right now.", "error");
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleGenerateWorkoutDescription = async () => {
    if (!selectedSplit) {
      setMessage("Select a workout first.", "error");
      return;
    }

    if (!selectedSplit.isRestSplit && !exercises.length) {
      setMessage("Add at least one exercise before generating a workout description.", "error");
      return;
    }

    setIsGeneratingWorkoutDescription(true);
    try {
      const exerciseSummaries = (exercises || [])
        .slice(0, 12)
        .map((exercise) => formatExerciseSummaryForPrompt(exercise));
      const promptPayload = {
        planName: plan?.name || "Untitled Training Plan",
        workoutName: selectedSplit.name || "Workout",
        workoutType: selectedSplit.isRestSplit ? "rest_day" : "workout",
        exerciseCount: exercises?.length || 0,
        exercises: exerciseSummaries,
      };
      const aiResponse = await getChatGPTResponse({
        temperature: 0.3,
        messages: [
          {
            role: "system",
            content:
              "You are a fitness programming assistant. Write concise coach-facing workout summaries.",
          },
          {
            role: "user",
            content:
              "Create a concise workout description in 1-3 sentences. Mention training focus and structure. Keep practical tone. Do not use markdown bullets.",
          },
          {
            role: "user",
            content: `Workout data JSON:\n${JSON.stringify(promptPayload, null, 2)}`,
          },
        ],
      });
      const generatedDescription = getAiResponseContent(aiResponse).trim();
      if (!generatedDescription) {
        throw new Error("AI returned an empty workout description.");
      }
      setSplitDescriptionDraft(generatedDescription);
      setMessage("AI workout description generated. Review and save when ready.");
    } catch (error) {
      setMessage(error?.message || "Unable to generate workout description right now.", "error");
    } finally {
      setIsGeneratingWorkoutDescription(false);
    }
  };

  const handleMoveExercise = async (exerciseId, direction) => {
    const currentIndex = exercises.findIndex((x) => x.id === exerciseId);
    if (currentIndex < 0) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= exercises.length) return;

    const reordered = [...exercises];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const withIndexes = reordered.map((item, index) => ({
      ...item,
      index,
      protocolId: planId,
      splitId: selectedSplitId,
    }));

    exercisesBySplitIdRef.current[selectedSplitId] = withIndexes;
    setExercises(withIndexes);
    if (useEditorStaging) {
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      savedExercisesHashRef.current = buildExercisesHash(withIndexes);
      setHasUnsavedExercises(false);
    } else {
      await reorderExercises(planId, withIndexes);
    }
    setMessage("Exercise order updated.");
  };

  const handleMoveSplit = async (splitId, direction) => {
    const currentIndex = splits.findIndex((x) => x.id === splitId);
    if (currentIndex < 0) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= splits.length) return;

    const reordered = [...splits];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const withIndexes = reordered.map((item, index) => ({
      ...item,
      protocolId: planId,
      index,
    }));

    setSplits(withIndexes);
    if (useEditorStaging) {
      await saveEditorStaging(planId, {
        plan,
        splits: withIndexes,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
    } else {
      await reorderSplits(planId, withIndexes);
    }
    setMessage("Workout order updated.");
  };

  const handleDuplicateWorkout = async () => {
    if (!planId || !selectedSplit) {
      setMessage("Select a workout first.", "error");
      return;
    }

    try {
      if (useEditorStaging) {
        flushExercisesToRef();
        const sourceRows =
          exercisesBySplitIdRef.current[selectedSplit.id] ||
          (selectedSplit.isRestSplit ? [] : exercises);
        const newSplitId = nanoid();
        const duplicatedSplit = {
          ...selectedSplit,
          id: newSplitId,
          name: `${selectedSplit.name || "Workout"} (copy)`,
          index: splits.length,
        };
        const nextSplits = [...splits, duplicatedSplit].map((s, i) => ({ ...s, index: i }));
        exercisesBySplitIdRef.current[newSplitId] = selectedSplit.isRestSplit
          ? []
          : remapExercisesForSplitCopy(sourceRows, newSplitId);
        setSplits(nextSplits);
        setSelectedSplitId(newSplitId);
        const nextEx = exercisesBySplitIdRef.current[newSplitId] || [];
        setExercises(nextEx);
        savedExercisesHashRef.current = buildExercisesHash(nextEx);
        setHasUnsavedExercises(false);
        await saveEditorStaging(planId, {
          plan,
          splits: nextSplits,
          exercisesBySplitId: { ...exercisesBySplitIdRef.current },
        });
        setHasStagingDoc(true);
        setMessage(`Duplicated ${selectedSplit.name || "workout"}.`);
        return;
      }

      const existingSplitIds = new Set((splits || []).map((split) => split.id));
      await duplicateSplit(planId, selectedSplit, splits.length);
      await appendRevision(planId, {
        action: "duplicate_workout",
        summary: `Duplicated ${selectedSplit.name || "workout"}`,
      });

      const updatedSplits = await getSplits(planId);
      setSplits(updatedSplits);

      const duplicatedSplit =
        updatedSplits.find((split) => !existingSplitIds.has(split.id)) ||
        updatedSplits.find((split) => split.index === splits.length) ||
        null;

      if (duplicatedSplit?.id) {
        await refreshExercises(duplicatedSplit.id);
      } else {
        await refreshExercises(selectedSplit.id);
      }

      setMessage(`Duplicated ${selectedSplit.name || "workout"}.`);
    } catch (error) {
      setMessage(error?.message || "Unable to duplicate workout.", "error");
    }
  };

  const handleDeleteWorkout = async () => {
    if (!planId || !selectedSplit) {
      setMessage("Select a workout first.", "error");
      return;
    }

    const confirmed = window.confirm(
      `Delete "${selectedSplit.name || "this workout"}"? This will also delete all exercises in it.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      const deletedSplitId = selectedSplit.id;
      const deletedSplitName = selectedSplit.name || "workout";
      const deletedIndex = splits.findIndex((split) => split.id === deletedSplitId);

      if (useEditorStaging) {
        flushExercisesToRef();
        const updatedSplits = splits
          .filter((split) => split.id !== deletedSplitId)
          .map((split, index) => ({ ...split, index }));
        delete exercisesBySplitIdRef.current[deletedSplitId];
        setSplits(updatedSplits);
        if (!updatedSplits.length) {
          setSelectedSplitId(null);
          setExercises([]);
          savedExercisesHashRef.current = buildExercisesHash([]);
          setHasUnsavedExercises(false);
        } else {
          const nextIndex =
            deletedIndex >= updatedSplits.length ? updatedSplits.length - 1 : deletedIndex;
          const nextSplit = updatedSplits[nextIndex];
          setSelectedSplitId(nextSplit.id);
          if (nextSplit.isRestSplit) {
            setExercises([]);
            savedExercisesHashRef.current = buildExercisesHash([]);
          } else {
            const cached = exercisesBySplitIdRef.current[nextSplit.id];
            const nextEx = Array.isArray(cached) ? [...cached] : [];
            setExercises(nextEx);
            savedExercisesHashRef.current = buildExercisesHash(nextEx);
          }
          setHasUnsavedExercises(false);
        }
        await saveEditorStaging(planId, {
          plan,
          splits: updatedSplits,
          exercisesBySplitId: { ...exercisesBySplitIdRef.current },
        });
        setHasStagingDoc(true);
        setMessage(`Deleted ${deletedSplitName}.`);
        return;
      }

      await deleteSplit(planId, deletedSplitId);
      await appendRevision(planId, {
        action: "delete_workout",
        summary: `Deleted ${deletedSplitName}`,
      });

      const updatedSplits = await getSplits(planId);
      setSplits(updatedSplits);

      if (!updatedSplits.length) {
        setSelectedSplitId(null);
        setExercises([]);
        savedExercisesHashRef.current = buildExercisesHash([]);
        setHasUnsavedExercises(false);
        setMessage(`Deleted ${deletedSplitName}.`);
        return;
      }

      const nextIndex = deletedIndex >= updatedSplits.length ? updatedSplits.length - 1 : deletedIndex;
      const nextSplit = updatedSplits[nextIndex];
      await refreshExercises(nextSplit.id);
      setMessage(`Deleted ${deletedSplitName}.`);
    } catch (error) {
      setMessage(error?.message || "Unable to delete workout.", "error");
    }
  };

  const handleSaveDraft = async () => {
    if (
      !isCreate &&
      normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft") === "archived"
    ) {
      setMessage(
        "This version is read-only. Use the Manage button in the Training Plans toolbar to restore or create a draft version before editing.",
        "error",
      );
      return;
    }
    if (!plan?.name?.trim()) {
      setMessage("Training plan name is required.", "error");
      return;
    }

    const effectivePlanType = effectivePlanTypeForSave;
    const effectiveClientUID = effectiveClientUIDForSave;

    if (effectivePlanType === "client" && !effectiveClientUID) {
      setMessage("Select a client before saving a client plan.", "error");
      return;
    }

    let currentPlanId = planId;
    if (isCreate) {
      const created = await createPlan({
        name: plan.name,
        description: plan.description,
        planType: effectivePlanType,
        clientUID: effectiveClientUID,
      });
      currentPlanId = created.id;
      setMessage("Draft created.");
      await trackTrainingPlansV2Event("plan_create", { planId: currentPlanId });
      navigate(
        withGuidedSearchPreserve(
          `/home/training-plans-v2/${currentPlanId}/edit`,
          location.search,
        ),
        { replace: true },
      );
      return;
    }

    try {
      await persistMetadataDraft();
      if (hasPersistableSplitDraft) {
        await persistCurrentSplitDraft();
      }
      if (hasUnsavedExercises) {
        await persistCurrentSplitExercises();
      }
      if (!useEditorStaging) {
        await appendRevision(currentPlanId, {
          action: "save_draft",
          summary: "Saved changes in editor",
        });
      }
      setLastSavedAt(new Date());
      setMessage(useEditorStaging ? "Staged changes saved." : "Changes saved.");
      trackTrainingPlansV2Event("plan_save_draft", { planId: currentPlanId });
    } catch (error) {
      setMessage(error?.message || "Unable to save changes.", "error");
    }
  };

  const handlePublish = async () => {
    if (
      !isCreate &&
      normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft") === "archived"
    ) {
      setMessage(
        "This version is read-only. Use the Manage button in the Training Plans toolbar to restore or create a draft version before publishing.",
        "error",
      );
      return;
    }
    if (!planId) {
      setMessage("Create and save the plan before publishing.", "error");
      return;
    }
    const lockedTemplate = initialPlanType === "template";
    const effectivePlanType = lockedTemplate ? "template" : plan.planType;
    const effectiveClientUID =
      effectivePlanType === "client" ? selectedClientUID || plan.clientUID || null : null;

    if (effectivePlanType === "client" && !effectiveClientUID) {
      setMessage("Select a client before publishing a client plan.", "error");
      return;
    }

    const normalizedLifecycle = normalizePlanLifecycleStatus(
      plan?.v2Meta?.status || plan?.status || "draft",
    );
    const isAssignedActive =
      normalizedLifecycle === "active" &&
      ((assignmentCoverage.primaryCount || 0) > 0 || (assignmentCoverage.alternativeCount || 0) > 0);

    if (isAssignedActive) {
      if (!assignmentCoverageReady) {
        setMessage("Loading assignment coverage… try again in a moment.", "warning");
        return;
      }
      if (!useEditorStaging) {
        setMessage("Unable to publish while the plan is still loading.", "error");
        return;
      }
      try {
        const shouldContinue = window.confirm(
          `Publish staged changes to the live plan?\n\nThis updates the plan for clients assigned to this version (primary: ${
            assignmentCoverage.primaryCount || 0
          }, alternative: ${
            assignmentCoverage.alternativeCount || 0
          }). Clients will be notified to sync.\n\nContinue?`,
        );
        if (!shouldContinue) {
          return;
        }
        setIsPublishing(true);
        await persistMetadataDraft();
        if (hasPersistableSplitDraft) {
          await persistCurrentSplitDraft();
        }
        if (hasUnsavedExercises) {
          await persistCurrentSplitExercises();
        }
        flushExercisesToRef();
        const snapshot = buildStagingSnapshot();
        await publishStagingSnapshotToLive(planId, snapshot);
        setLastSavedAt(new Date());
        setHasUnsavedMetadata(false);
        setHasUnsavedExercises(false);
        setHasStagingDoc(false);
        savedMetadataHashRef.current = buildMetadataHash({
          plan: {
            ...plan,
            planType: effectivePlanTypeForSave,
            clientUID: effectiveClientUIDForSave,
          },
          initialPlanType,
          selectedClientUID,
        });
        savedExercisesHashRef.current = buildExercisesHash(exercises);
        setMessage("Published staged changes to the live plan. Clients notified to sync.");
        trackTrainingPlansV2Event("plan_publish", {
          planId,
          path: "staging_assigned_active",
        });
        await loadData({ silent: true, preserveSplitId: selectedSplitId, reloadFromLiveOnly: true });
      } catch (error) {
        setMessage(error?.message || "Unable to publish staged changes.", "error");
      } finally {
        setIsPublishing(false);
      }
      return;
    }

    try {
      setIsPublishing(true);
      await savePlanMetadata(planId, {
        ...plan,
        planType: effectivePlanType,
        clientUID: effectiveClientUID,
        v2Meta: { ...(plan.v2Meta || {}), status: "draft" },
      });
      const publishPreview = await getPublishImpactPreview({ draftPlanId: planId });
      setIsPublishing(false);
      const shouldContinue = window.confirm(
        `Publish this version?\n\nPrevious active version: ${
          publishPreview?.previousActiveVersion
            ? `V${publishPreview.previousActiveVersion?.v2Meta?.versionNumber ?? "?"}`
            : "None detected"
        }\nEstimated primary client migrations: ${
          publishPreview?.migratedClientCountEstimate || 0
        }\n\nAlternative assignments are not migrated by publish.`,
      );
      if (!shouldContinue) {
        return;
      }
      setIsPublishing(true);
      const publishResult = await publishVersionDraft({ draftPlanId: planId });
      setMessage(
        `Plan published. Migrated ${
          publishResult?.migratedClientCount || 0
        } primary assignment(s) from the previous active version. Alternative assignments were unchanged.`,
      );
      trackTrainingPlansV2Event("plan_publish", {
        planId,
        migratedClientCount: publishResult?.migratedClientCount || 0,
      });
      try {
        await loadData({ silent: true, preserveSplitId: selectedSplitId, reloadFromLiveOnly: true });
      } catch {
        /* Publish succeeded; loadData already set a message if refresh failed */
      }
    } catch (error) {
      setMessage(error?.message || "Unable to publish this version.", "error");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveAsNewDraftVersion = React.useCallback(async () => {
    setOverflowMenuAnchor(null);
    if (
      !isCreate &&
      normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft") === "archived"
    ) {
      setMessage(
        "This version is read-only. Use the Manage button in the Training Plans toolbar to restore or create a draft version before editing.",
        "error",
      );
      return;
    }
    if (!plan?.name?.trim()) {
      setMessage("Training plan name is required.", "error");
      return;
    }
    if (effectivePlanTypeForSave === "client" && !effectiveClientUIDForSave) {
      setMessage("Select a client before saving a client plan.", "error");
      return;
    }
    if (isCreate || !planId) {
      setMessage("Save the plan first, then use Save as new draft version.", "error");
      return;
    }
    if (
      useEditorStaging &&
      (hasStagingDoc || hasUnsavedMetadata || hasUnsavedExercises || splitDraftDirty)
    ) {
      setMessage("Publish or revert staged changes before using Save as new draft version.", "error");
      return;
    }

    setIsSavingAsDraftVersion(true);
    try {
      await persistMetadataDraft();
      if (hasPersistableSplitDraft) {
        await persistCurrentSplitDraft();
      }
      if (hasUnsavedExercises) {
        await persistCurrentSplitExercises();
      }
      const newDraft = await createVersionDraft({
        sourcePlanId: planId,
        summary: "Saved as new draft version from editor",
      });
      await appendRevision(planId, {
        action: "save_draft",
        summary: "Saved before creating new version draft",
      });
      setLastSavedAt(new Date());
      setHasUnsavedMetadata(false);
      setHasUnsavedExercises(false);
      savedMetadataHashRef.current = buildMetadataHash({
        plan: {
          ...plan,
          planType: effectivePlanTypeForSave,
          clientUID: effectiveClientUIDForSave,
        },
        initialPlanType,
        selectedClientUID,
      });
      savedExercisesHashRef.current = buildExercisesHash(exercises);
      setMessage(
        `Created draft V${newDraft?.v2Meta?.versionNumber ?? "?"} — opening editor.`,
      );
      trackTrainingPlansV2Event("plan_save_as_new_draft_version", {
        planId,
        newDraftPlanId: newDraft.id,
      });
      navigate(
        withGuidedSearchPreserve(`/home/training-plans-v2/${newDraft.id}/edit`, location.search),
      );
    } catch (error) {
      setMessage(error?.message || "Unable to save as new draft version.", "error");
    } finally {
      setIsSavingAsDraftVersion(false);
    }
  }, [
    effectiveClientUIDForSave,
    effectivePlanTypeForSave,
    exercises,
    hasPersistableSplitDraft,
    hasUnsavedExercises,
    initialPlanType,
    isCreate,
    location.search,
    navigate,
    persistCurrentSplitDraft,
    persistCurrentSplitExercises,
    persistMetadataDraft,
    plan,
    planId,
    selectedClientUID,
    useEditorStaging,
    hasStagingDoc,
    hasUnsavedMetadata,
    setMessage,
    splitDraftDirty,
  ]);

  const handleOpenCopyWorkoutDialog = () => {
    if (!planId) {
      setPlanStructureBlockNotice("Save plan first, then add workouts.");
      return;
    }
    const clientUID =
      (plan?.planType || initialPlanType) === "client"
        ? selectedClientUID || plan?.clientUID || ""
        : "";
    if (!clientUID) {
      setMessage("Copy from workout is only available on client plans.", "error");
      return;
    }
    setPlanStructureBlockNotice("");
    setShowCopyWorkoutDialog(true);
  };

  const handleCopyWorkoutFromPlan = async ({ sourcePlanId, sourcePlanName, sourceSplit }) => {
    if (!planId) {
      setPlanStructureBlockNotice("Save plan first, then add workouts.");
      throw new Error("Save plan first, then add workouts.");
    }
    if (!sourceSplit?.id) {
      throw new Error("Select a workout to copy.");
    }

    const sourceLabel = sourcePlanName || sourcePlanId || "another plan";
    const workoutLabel = sourceSplit.name || "workout";

    if (useEditorStaging) {
      flushExercisesToRef();
      const sourceRows = sourceSplit.isRestSplit ? [] : await getSplitExercises(sourceSplit.id);
      const newSplitId = nanoid();
      const copiedSplit = {
        ...sourceSplit,
        id: newSplitId,
        protocolId: planId,
        name: sourceSplit.name || `Workout ${splits.length + 1}`,
        index: splits.length,
        isDuplicate: true,
        duplicatedOn: new Date(),
      };
      const nextSplits = [...splits, copiedSplit].map((s, i) => ({ ...s, index: i }));
      exercisesBySplitIdRef.current[newSplitId] = sourceSplit.isRestSplit
        ? []
        : remapExercisesForSplitCopy(sourceRows, newSplitId);
      setSplits(nextSplits);
      setSelectedSplitId(newSplitId);
      const nextEx = exercisesBySplitIdRef.current[newSplitId] || [];
      setExercises(nextEx);
      savedExercisesHashRef.current = buildExercisesHash(nextEx);
      setHasUnsavedExercises(false);
      await saveEditorStaging(planId, {
        plan,
        splits: nextSplits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setMessage(`Copied ${workoutLabel} from ${sourceLabel}.`);
      await trackTrainingPlansV2Event("copy_workout_from_plan", {
        planId,
        sourcePlanId,
        splitId: newSplitId,
      });
      return;
    }

    const existingSplitIds = new Set((splits || []).map((split) => split.id));
    await copySplitIntoPlan({
      targetPlanId: planId,
      sourceSplit,
      index: splits.length,
    });
    await appendRevision(planId, {
      action: "copy_workout_from_plan",
      summary: `Copied ${workoutLabel} from ${sourceLabel}`,
    });
    await trackTrainingPlansV2Event("copy_workout_from_plan", {
      planId,
      sourcePlanId,
    });

    const updatedSplits = await getSplits(planId);
    setSplits(updatedSplits);

    const copied =
      updatedSplits.find((split) => !existingSplitIds.has(split.id)) ||
      updatedSplits.find((split) => split.index === splits.length) ||
      null;

    if (copied?.id) {
      await refreshExercises(copied.id);
    }

    setMessage(`Copied ${workoutLabel} from ${sourceLabel}.`);
  };

  const handleAddWorkout = async () => {
    if (!planId) {
      setPlanStructureBlockNotice("Save plan first, then add workouts.");
      return;
    }
    setPlanStructureBlockNotice("");
    const split = {
      id: nanoid(),
      protocolId: planId,
      name: `Workout ${splits.length + 1}`,
      description: "",
      index: splits.length,
      labels: [],
      isRestSplit: false,
    };
    if (useEditorStaging) {
      flushExercisesToRef();
      const nextSplits = [...splits, split].map((s, i) => ({ ...s, index: i }));
      exercisesBySplitIdRef.current[split.id] = [];
      setSplits(nextSplits);
      await saveEditorStaging(planId, {
        plan,
        splits: nextSplits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setSelectedSplitId(split.id);
      setExercises([]);
      savedExercisesHashRef.current = buildExercisesHash([]);
      setHasUnsavedExercises(false);
      return;
    }
    await upsertSplit(split, true);
    await appendRevision(planId, { action: "add_workout", summary: `Added ${split.name}` });
    const nextSplits = await getSplits(planId);
    setSplits(nextSplits);
    setSelectedSplitId(split.id);
    const loaded = nextSplits.find((s) => s.id === split.id);
    if (loaded?.isRestSplit) {
      setExercises([]);
      savedExercisesHashRef.current = buildExercisesHash([]);
      setHasUnsavedExercises(false);
    } else {
      const splitExercises = await getSplitExercises(split.id);
      const normalizedExercises = stripRepsMaxFromExercises(splitExercises);
      setExercises(normalizedExercises);
      savedExercisesHashRef.current = buildExercisesHash(normalizedExercises);
      setHasUnsavedExercises(false);
    }
  };

  const handleAddRestDay = async () => {
    if (!planId) {
      setPlanStructureBlockNotice("Save plan first, then add rest days.");
      return;
    }
    setPlanStructureBlockNotice("");
    const split = {
      id: nanoid(),
      protocolId: planId,
      name: `Rest Day ${splits.length + 1}`,
      description: "Take it easy today.",
      index: splits.length,
      labels: [],
      isRestSplit: true,
      restDays: 1,
    };
    if (useEditorStaging) {
      flushExercisesToRef();
      const nextSplits = [...splits, split].map((s, i) => ({ ...s, index: i }));
      exercisesBySplitIdRef.current[split.id] = [];
      setSplits(nextSplits);
      await saveEditorStaging(planId, {
        plan,
        splits: nextSplits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setSelectedSplitId(split.id);
      setExercises([]);
      savedExercisesHashRef.current = buildExercisesHash([]);
      setHasUnsavedExercises(false);
      return;
    }
    await upsertSplit(split, true);
    await appendRevision(planId, {
      action: "add_rest_day",
      summary: `Added ${split.name}`,
    });
    const nextSplits = await getSplits(planId);
    setSplits(nextSplits);
    setSelectedSplitId(split.id);
    setExercises([]);
    savedExercisesHashRef.current = buildExercisesHash([]);
    setHasUnsavedExercises(false);
  };

  const handleAddExercise = async () => {
    if (!planId || !selectedSplitId) {
      setMessage("Select a workout first.", "error");
      return;
    }
    if (selectedSplit?.isRestSplit) {
      setMessage(
        "Rest days don't include exercises. Edit the name and description under Workout Details.",
        "error",
      );
      return;
    }
    const item = {
      id: nanoid(),
      protocolId: planId,
      splitId: selectedSplitId,
      type: WorkoutTypes.normalSet,
      exercise: null,
      superSetName: "",
      sets: 3,
      setsToFailure: 0,
      reps: 8,
      tempo: "",
      rest: "60s",
      notes: "",
      labels: [],
      prescriptionType: DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
      durationSeconds: null,
      durationValue: "",
      distanceKilometers: null,
      distanceValue: "",
      intervalWorkSeconds: null,
      intervalWorkValue: "",
      intervalRestSeconds: null,
      intervalRestValue: "",
      intervalRounds: null,
      exercises: [],
      index: exercises.length,
    };
    setPendingScrollExerciseId(item.id);
    if (useEditorStaging) {
      const next = [...exercises, item];
      exercisesBySplitIdRef.current[selectedSplitId] = next;
      setExercises(next);
      savedExercisesHashRef.current = buildExercisesHash(next);
      setHasUnsavedExercises(false);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      return;
    }
    await upsertExercise(item);
    await appendRevision(planId, { action: "add_exercise", summary: "Added exercise row" });
    await refreshExercises(selectedSplitId);
  };

  const handleAddSuperset = async () => {
    if (!planId || !selectedSplitId) {
      setMessage("Select a workout first.", "error");
      return;
    }
    if (selectedSplit?.isRestSplit) {
      setMessage(
        "Rest days don't include exercises. Edit the name and description under Workout Details.",
        "error",
      );
      return;
    }
    const existingSupersets = exercises.filter((x) => x.type === WorkoutTypes.superSet).length;
    const item = {
      id: nanoid(),
      protocolId: planId,
      splitId: selectedSplitId,
      type: WorkoutTypes.superSet,
      exercise: WorkoutTypes.superSet,
      superSetName: `Superset ${existingSupersets + 1}`,
      sets: 3,
      setsToFailure: 0,
      reps: 8,
      tempo: "",
      rest: "60s",
      notes: "",
      labels: [],
      prescriptionType: DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
      durationSeconds: null,
      durationValue: "",
      distanceKilometers: null,
      distanceValue: "",
      intervalWorkSeconds: null,
      intervalWorkValue: "",
      intervalRestSeconds: null,
      intervalRestValue: "",
      intervalRounds: null,
      exercises: [createSupersetChild()],
      index: exercises.length,
    };
    setPendingScrollExerciseId(item.id);
    if (useEditorStaging) {
      const next = [...exercises, item];
      exercisesBySplitIdRef.current[selectedSplitId] = next;
      setExercises(next);
      savedExercisesHashRef.current = buildExercisesHash(next);
      setHasUnsavedExercises(false);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      return;
    }
    await upsertExercise(item);
    await appendRevision(planId, { action: "add_superset", summary: "Added superset row" });
    await refreshExercises(selectedSplitId);
  };

  const handleAddToBulkQueue = () => {
    if (!bulkAddDraftExerciseId) return;
    const option = exerciseOptions.find((exercise) => exercise.id === bulkAddDraftExerciseId);
    if (!option) return;
    setBulkAddQueue((prev) => [...prev, option]);
    setBulkAddDraftExerciseId(null);
  };

  const handleRemoveBulkQueueItem = (indexToRemove) => {
    setBulkAddQueue((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleApplyBulkAddQueue = async () => {
    if (!planId || !selectedSplitId || !bulkAddQueue.length) {
      setShowBulkAddDialog(false);
      return;
    }
    if (selectedSplit?.isRestSplit) {
      setMessage("Rest days don't include exercises.", "error");
      setShowBulkAddDialog(false);
      return;
    }
    const startIndex = exercises.length;
    const rows = bulkAddQueue.map((queued, index) => ({
      id: nanoid(),
      protocolId: planId,
      splitId: selectedSplitId,
      type: WorkoutTypes.normalSet,
      exercise: queued.id,
      superSetName: "",
      sets: 3,
      setsToFailure: 0,
      reps: 8,
      tempo: "",
      rest: "60s",
      notes: "",
      labels: [],
      prescriptionType: DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
      durationSeconds: null,
      durationValue: "",
      distanceKilometers: null,
      distanceValue: "",
      intervalWorkSeconds: null,
      intervalWorkValue: "",
      intervalRestSeconds: null,
      intervalRestValue: "",
      intervalRounds: null,
      exercises: [],
      index: startIndex + index,
    }));
    if (useEditorStaging) {
      const next = [...exercises, ...rows];
      exercisesBySplitIdRef.current[selectedSplitId] = next;
      setExercises(next);
      savedExercisesHashRef.current = buildExercisesHash(next);
      setHasUnsavedExercises(false);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setBulkAddQueue([]);
      setShowBulkAddDialog(false);
      setMessage(`Added ${rows.length} exercise row(s).`);
      return;
    }
    await bulkUpsertExercises(rows);
    await appendRevision(planId, {
      action: "bulk_add_exercises",
      summary: `Bulk added ${rows.length} exercise row(s)`,
    });
    setBulkAddQueue([]);
    setShowBulkAddDialog(false);
    await refreshExercises(selectedSplitId);
    setMessage(`Added ${rows.length} exercise row(s).`);
  };

  const handleDuplicateExercise = async (exerciseId) => {
    if (!planId || !selectedSplitId) return;
    const sourceIndex = exercises.findIndex((exercise) => exercise.id === exerciseId);
    if (sourceIndex < 0) return;
    const source = exercises[sourceIndex];
    const duplicate = {
      ...source,
      id: nanoid(),
      protocolId: planId,
      splitId: selectedSplitId,
      index: sourceIndex + 1,
      exercises: (source.exercises || []).map((child) => ({ ...child, id: nanoid() })),
    };
    const nextExercises = [...exercises];
    nextExercises.splice(sourceIndex + 1, 0, duplicate);
    const reIndexed = nextExercises.map((item, index) => ({ ...item, index }));
    if (useEditorStaging) {
      exercisesBySplitIdRef.current[selectedSplitId] = reIndexed;
      setExercises(reIndexed);
      savedExercisesHashRef.current = buildExercisesHash(reIndexed);
      setHasUnsavedExercises(false);
      setPendingScrollExerciseId(duplicate.id);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setMessage("Exercise row duplicated.");
      return;
    }
    await bulkUpsertExercises(
      reIndexed.map((item) => ({
        ...item,
        protocolId: planId,
        splitId: selectedSplitId,
      })),
    );
    setPendingScrollExerciseId(duplicate.id);
    await appendRevision(planId, {
      action: "duplicate_exercise_row",
      summary: "Duplicated exercise row",
    });
    await refreshExercises(selectedSplitId);
    setMessage("Exercise row duplicated.");
  };

  const toggleExerciseSelection = (exerciseId) => {
    setSelectedExerciseIds((prev) =>
      prev.includes(exerciseId) ? prev.filter((id) => id !== exerciseId) : [...prev, exerciseId],
    );
  };

  const toggleSelectAllExercises = () => {
    if (selectedExerciseIds.length > 0) {
      setSelectedExerciseIds([]);
      return;
    }
    setSelectedExerciseIds(exercises.map((exercise) => exercise.id));
  };

  const handleOpenBulkEditDialog = () => {
    if (!selectedExerciseIds.length) return;
    setBulkEditPreset("none");
    setBulkEditDraft({
      sets: "",
      reps: "",
      rest: "",
      tempo: "",
      notes: "",
    });
    setShowBulkEditDialog(true);
  };

  const handleBulkPresetChange = (presetKey) => {
    const preset = BULK_EDIT_PRESETS[presetKey] || {};
    setBulkEditPreset(presetKey);
    setBulkEditDraft((prev) => ({
      ...prev,
      ...preset,
    }));
  };

  const handleApplyBulkEdit = () => {
    const selectedSet = new Set(selectedExerciseIds);
    if (!selectedSet.size) return;
    const patch = Object.entries(bulkEditDraft).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
    if (!Object.keys(patch).length) {
      setShowBulkEditDialog(false);
      return;
    }
    const numericFields = new Set(["sets", "reps"]);
    const normalizedPatch = Object.entries(patch).reduce((acc, [key, value]) => {
      if (numericFields.has(key)) {
        const parsed = parseInt(String(value), 10);
        if (!Number.isNaN(parsed)) {
          acc[key] = parsed;
        }
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});
    if (!Object.keys(normalizedPatch).length) {
      setShowBulkEditDialog(false);
      return;
    }
    setExercises((prev) =>
      prev.map((exercise) =>
        selectedSet.has(exercise.id) ? { ...exercise, ...normalizedPatch } : exercise,
      ),
    );
    setShowBulkEditDialog(false);
    setMessage(`Applied bulk edit to ${selectedSet.size} exercise row(s).`);
  };

  const handleBulkDeleteSelected = async () => {
    if (!planId || !selectedSplitId || !selectedExerciseIds.length) return;
    const selectedSet = new Set(selectedExerciseIds);
    const toDelete = exercises
      .filter((exercise) => selectedSet.has(exercise.id))
      .map((exercise) => ({ id: exercise.id, protocolId: planId, splitId: selectedSplitId }));
    if (!toDelete.length) return;
    if (useEditorStaging) {
      const remaining = exercises.filter((exercise) => !selectedSet.has(exercise.id));
      const reIndexed = remaining.map((item, index) => ({ ...item, index }));
      exercisesBySplitIdRef.current[selectedSplitId] = reIndexed;
      setExercises(reIndexed);
      savedExercisesHashRef.current = buildExercisesHash(reIndexed);
      setHasUnsavedExercises(false);
      setSelectedExerciseIds([]);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setMessage(`Deleted ${toDelete.length} exercise row(s).`);
      return;
    }
    await bulkDeleteExercises(toDelete);
    await appendRevision(planId, {
      action: "bulk_delete_exercises",
      summary: `Deleted ${toDelete.length} exercise row(s)`,
    });
    setSelectedExerciseIds([]);
    const refreshed = await getSplitExercises(selectedSplitId);
    const reIndexed = refreshed.map((item, index) => ({
      ...item,
      index,
      protocolId: planId,
      splitId: selectedSplitId,
    }));
    await reorderExercises(planId, reIndexed);
    setExercises(reIndexed);
    savedExercisesHashRef.current = buildExercisesHash(reIndexed);
    setHasUnsavedExercises(false);
    setMessage(`Deleted ${toDelete.length} exercise row(s).`);
  };

  const handleDeleteExerciseRow = async (exerciseId) => {
    if (!planId || !selectedSplitId) return;
    if (useEditorStaging) {
      const next = exercises
        .filter((x) => x.id !== exerciseId)
        .map((x, i) => ({ ...x, index: i }));
      exercisesBySplitIdRef.current[selectedSplitId] = next;
      setExercises(next);
      savedExercisesHashRef.current = buildExercisesHash(next);
      setHasUnsavedExercises(false);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setMessage("Exercise deleted.");
      return;
    }
    await deleteExercise({
      id: exerciseId,
      protocolId: planId,
      splitId: selectedSplitId,
    });
    await refreshExercises(selectedSplitId);
    setMessage("Exercise deleted.");
  };

  const handleSaveExerciseRow = async (exerciseRow) => {
    if (!planId || !selectedSplitId) return;
    if (useEditorStaging) {
      const next = exercises.map((x) => (x.id === exerciseRow.id ? exerciseRow : x));
      exercisesBySplitIdRef.current[selectedSplitId] = next;
      setExercises(next);
      savedExercisesHashRef.current = buildExercisesHash(next);
      setHasUnsavedExercises(false);
      await saveEditorStaging(planId, {
        plan,
        splits,
        exercisesBySplitId: { ...exercisesBySplitIdRef.current },
      });
      setHasStagingDoc(true);
      setLastSavedAt(new Date());
      setMessage("Exercise saved.");
      return;
    }
    await upsertExercise({
      ...exerciseRow,
      protocolId: planId,
      splitId: selectedSplitId,
    });
    savedExercisesHashRef.current = buildExercisesHash(exercises);
    setHasUnsavedExercises(false);
    setLastSavedAt(new Date());
    setMessage("Exercise saved.");
  };

  function createSupersetChild() {
    return {
      id: nanoid(),
      exercise: null,
      reps: 8,
      tempo: "",
      rest: "60s",
      notes: "",
      prescriptionType: DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
      durationSeconds: null,
      durationValue: "",
      distanceKilometers: null,
      distanceValue: "",
      intervalWorkSeconds: null,
      intervalWorkValue: "",
      intervalRestSeconds: null,
      intervalRestValue: "",
      intervalRounds: null,
    };
  }

  function createAlternativeFromPrimary(primaryExercise) {
    const base = {
      id: nanoid(),
      type: WorkoutTypes.normalSet,
      exercise: null,
      exerciseName: null,
      exercises: [],
      sets: primaryExercise.sets ?? 3,
      reps: primaryExercise.reps ?? "",
      tempo: primaryExercise.tempo ?? "",
      rest: primaryExercise.rest ?? "",
      notes: "",
      prescriptionType: primaryExercise.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
      durationValue: primaryExercise.durationValue ?? "",
      distanceValue: primaryExercise.distanceValue ?? "",
      intervalWorkValue: primaryExercise.intervalWorkValue ?? "",
      intervalRestValue: primaryExercise.intervalRestValue ?? "",
      intervalRounds: primaryExercise.intervalRounds ?? null,
    };
    return base;
  }

  const addAlternative = (exerciseId) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const newAlt = createAlternativeFromPrimary(entry);
        return { ...entry, alternatives: [...(entry.alternatives || []), newAlt] };
      }),
    );
    setShowAlternativesByExercise((prev) => ({ ...prev, [exerciseId]: true }));
  };

  const updateAlternative = (exerciseId, altId, patch) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const alts = (entry.alternatives || []).map((alt) =>
          alt.id === altId ? { ...alt, ...patch } : alt,
        );
        return { ...entry, alternatives: alts };
      }),
    );
  };

  const removeAlternative = (exerciseId, altId) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        return { ...entry, alternatives: (entry.alternatives || []).filter((a) => a.id !== altId) };
      }),
    );
  };

  const moveAlternative = (exerciseId, altId, direction) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const alts = [...(entry.alternatives || [])];
        const idx = alts.findIndex((a) => a.id === altId);
        if (idx === -1) return entry;
        const nextIdx = direction === "up" ? idx - 1 : idx + 1;
        if (nextIdx < 0 || nextIdx >= alts.length) return entry;
        [alts[idx], alts[nextIdx]] = [alts[nextIdx], alts[idx]];
        return { ...entry, alternatives: alts };
      }),
    );
  };

  const addAlternativeSupersetChild = (exerciseId, altId) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const alts = (entry.alternatives || []).map((alt) => {
          if (alt.id !== altId) return alt;
          return { ...alt, exercises: [...(alt.exercises || []), createSupersetChild()] };
        });
        return { ...entry, alternatives: alts };
      }),
    );
  };

  const updateAlternativeSupersetChild = (exerciseId, altId, childId, patch) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const alts = (entry.alternatives || []).map((alt) => {
          if (alt.id !== altId) return alt;
          const children = (alt.exercises || []).map((c) =>
            c.id === childId ? { ...c, ...patch } : c,
          );
          return { ...alt, exercises: children };
        });
        return { ...entry, alternatives: alts };
      }),
    );
  };

  const removeAlternativeSupersetChild = (exerciseId, altId, childId) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const alts = (entry.alternatives || []).map((alt) => {
          if (alt.id !== altId) return alt;
          const remaining = (alt.exercises || []).filter((c) => c.id !== childId);
          return { ...alt, exercises: remaining.length > 0 ? remaining : [createSupersetChild()] };
        });
        return { ...entry, alternatives: alts };
      }),
    );
  };

  const updateSupersetChild = (exerciseId, childId, patch) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const children = (entry.exercises || []).map((child) =>
          child.id === childId ? { ...child, ...patch } : child,
        );
        return { ...entry, exercises: children };
      }),
    );
  };

  const addSupersetChild = (exerciseId) => {
    const newChild = createSupersetChild();
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        return { ...entry, exercises: [...(entry.exercises || []), newChild] };
      }),
    );
  };

  const removeSupersetChild = (exerciseId, childId) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const remaining = (entry.exercises || []).filter((child) => child.id !== childId);
        return {
          ...entry,
          exercises: remaining.length > 0 ? remaining : [createSupersetChild()],
        };
      }),
    );
  };

  const duplicateSupersetChild = (exerciseId, childId) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const children = entry.exercises || [];
        const childIndex = children.findIndex((child) => child.id === childId);
        if (childIndex < 0) return entry;
        const duplicate = { ...children[childIndex], id: nanoid() };
        const nextChildren = [...children];
        nextChildren.splice(childIndex + 1, 0, duplicate);
        return { ...entry, exercises: nextChildren };
      }),
    );
  };

  const moveSupersetChild = (exerciseId, childId, direction) => {
    setExercises((prev) =>
      prev.map((entry) => {
        if (entry.id !== exerciseId) return entry;
        const children = [...(entry.exercises || [])];
        const currentIndex = children.findIndex((child) => child.id === childId);
        if (currentIndex < 0) return entry;
        const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= children.length) return entry;
        const [moved] = children.splice(currentIndex, 1);
        children.splice(targetIndex, 0, moved);
        return { ...entry, exercises: children };
      }),
    );
  };

  const renderPrescriptionFields = (exercise) => {
    const prescriptionType =
      exercise.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE;
    if (prescriptionType === ExercisePrescriptionTypes.DURATION) {
      return (
        <TextField
          size="small"
          label="Duration"
          placeholder="e.g. 90s / 2 min"
          value={exercise.durationValue || ""}
          onChange={(e) => {
            const value = e.target.value;
            setExercises((prev) =>
              prev.map((x) => (x.id === exercise.id ? { ...x, durationValue: value } : x)),
            );
          }}
        />
      );
    }
    if (prescriptionType === ExercisePrescriptionTypes.DISTANCE) {
      return (
        <TextField
          size="small"
          label="Distance"
          placeholder="e.g. 3 km / 1 mile"
          value={exercise.distanceValue || ""}
          onChange={(e) => {
            const value = e.target.value;
            setExercises((prev) =>
              prev.map((x) => (x.id === exercise.id ? { ...x, distanceValue: value } : x)),
            );
          }}
        />
      );
    }
    if (prescriptionType === ExercisePrescriptionTypes.INTERVAL) {
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <TextField
            size="small"
            label="Work"
            placeholder="e.g. 30s"
            value={exercise.intervalWorkValue || ""}
            onChange={(e) => {
              const value = e.target.value;
              setExercises((prev) =>
                prev.map((x) =>
                  x.id === exercise.id ? { ...x, intervalWorkValue: value } : x,
                ),
              );
            }}
          />
          <TextField
            size="small"
            label="Rest Interval"
            placeholder="e.g. 30s"
            value={exercise.intervalRestValue || ""}
            onChange={(e) => {
              const value = e.target.value;
              setExercises((prev) =>
                prev.map((x) =>
                  x.id === exercise.id ? { ...x, intervalRestValue: value } : x,
                ),
              );
            }}
          />
          <TextField
            size="small"
            type="number"
            label="Rounds"
            value={exercise.intervalRounds ?? ""}
            onChange={(e) => {
              const value = parseInt(e.target.value || "0", 10);
              setExercises((prev) =>
                prev.map((x) =>
                  x.id === exercise.id ? { ...x, intervalRounds: value } : x,
                ),
              );
            }}
          />
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label="Reps"
          placeholder="e.g. 8-10 or 12-8-6-4"
          value={exercise.reps ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            setExercises((prev) =>
              prev.map((x) => (x.id === exercise.id ? { ...x, reps: value } : x)),
            );
          }}
        />
      </Stack>
    );
  };

  const renderAlternativePrescriptionFields = (exerciseId, alt) => {
    const prescriptionType = alt.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE;
    if (prescriptionType === ExercisePrescriptionTypes.DURATION) {
      return (
        <TextField
          size="small"
          label="Duration"
          placeholder="e.g. 90s / 2 min"
          value={alt.durationValue || ""}
          onChange={(e) => updateAlternative(exerciseId, alt.id, { durationValue: e.target.value })}
        />
      );
    }
    if (prescriptionType === ExercisePrescriptionTypes.DISTANCE) {
      return (
        <TextField
          size="small"
          label="Distance"
          placeholder="e.g. 3 km / 1 mile"
          value={alt.distanceValue || ""}
          onChange={(e) => updateAlternative(exerciseId, alt.id, { distanceValue: e.target.value })}
        />
      );
    }
    if (prescriptionType === ExercisePrescriptionTypes.INTERVAL) {
      return (
        <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
          <TextField
            size="small"
            label="Work"
            placeholder="e.g. 30s"
            value={alt.intervalWorkValue || ""}
            onChange={(e) => updateAlternative(exerciseId, alt.id, { intervalWorkValue: e.target.value })}
          />
          <TextField
            size="small"
            label="Rest Interval"
            placeholder="e.g. 30s"
            value={alt.intervalRestValue || ""}
            onChange={(e) => updateAlternative(exerciseId, alt.id, { intervalRestValue: e.target.value })}
          />
          <TextField
            size="small"
            type="number"
            label="Rounds"
            value={alt.intervalRounds ?? ""}
            onChange={(e) => updateAlternative(exerciseId, alt.id, { intervalRounds: parseInt(e.target.value || "0", 10) })}
          />
        </Box>
      );
    }
    // Default: Reps / Sets
    return (
      <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
        <TextField
          size="small"
          label="Sets"
          type="number"
          value={alt.sets || 0}
          onChange={(e) => updateAlternative(exerciseId, alt.id, { sets: parseInt(e.target.value || "0", 10) })}
        />
        <TextField
          size="small"
          label="Reps"
          placeholder="e.g. 8-10"
          value={alt.reps ?? ""}
          onChange={(e) => updateAlternative(exerciseId, alt.id, { reps: e.target.value })}
        />
        <TextField
          size="small"
          label="Rest"
          placeholder="e.g. 60s"
          value={alt.rest || ""}
          onChange={(e) => updateAlternative(exerciseId, alt.id, { rest: e.target.value })}
        />
        <TextField
          size="small"
          label="Tempo"
          value={alt.tempo || ""}
          onChange={(e) => updateAlternative(exerciseId, alt.id, { tempo: e.target.value })}
        />
      </Box>
    );
  };

  const renderSupersetChildPrescriptionFields = (exerciseId, child) => {
    const prescriptionType =
      child.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE;
    if (prescriptionType === ExercisePrescriptionTypes.DURATION) {
      return (
        <TextField
          fullWidth
          size="small"
          label="Duration"
          placeholder="e.g. 90s / 2 min"
          value={child.durationValue || ""}
          onChange={(e) => {
            updateSupersetChild(exerciseId, child.id, { durationValue: e.target.value });
          }}
        />
      );
    }
    if (prescriptionType === ExercisePrescriptionTypes.DISTANCE) {
      return (
        <TextField
          fullWidth
          size="small"
          label="Distance"
          placeholder="e.g. 3 km / 1 mile"
          value={child.distanceValue || ""}
          onChange={(e) => {
            updateSupersetChild(exerciseId, child.id, { distanceValue: e.target.value });
          }}
        />
      );
    }
    if (prescriptionType === ExercisePrescriptionTypes.INTERVAL) {
      return (
        <>
          <TextField
            fullWidth
            size="small"
            label="Work"
            placeholder="e.g. 30s"
            value={child.intervalWorkValue || ""}
            onChange={(e) => {
              updateSupersetChild(exerciseId, child.id, { intervalWorkValue: e.target.value });
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Rest Interval"
            placeholder="e.g. 30s"
            value={child.intervalRestValue || ""}
            onChange={(e) => {
              updateSupersetChild(exerciseId, child.id, { intervalRestValue: e.target.value });
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Rounds"
            type="number"
            value={child.intervalRounds ?? ""}
            onChange={(e) => {
              const value = parseInt(e.target.value || "0", 10);
              updateSupersetChild(exerciseId, child.id, { intervalRounds: value });
            }}
          />
        </>
      );
    }
    return (
      <>
        <TextField
          fullWidth
          size="small"
          label="Reps"
          placeholder="e.g. 8-10 or 12-8-6-4"
          value={child.reps ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            updateSupersetChild(exerciseId, child.id, { reps: value });
          }}
        />
      </>
    );
  };

  const availableClients = React.useMemo(() => {
    const activeClients = clients.filter((client) => {
      const status = (client.status || "active").toLowerCase();
      return status !== "disabled" && status !== "inactive";
    });
    const source = showInactiveClients ? clients : activeClients;
    return [...source].sort((a, b) => {
      const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim();
      const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
      return aName.localeCompare(bName);
    });
  }, [clients, showInactiveClients]);

  const selectedClient = React.useMemo(() => {
    const selectedId = selectedClientUID || plan?.clientUID || "";
    if (!selectedId) return null;
    return clients.find((client) => client.uid === selectedId) || null;
  }, [clients, plan?.clientUID, selectedClientUID]);
  const isClientOwnerLocked = !isCreate && initialPlanType === "client" && plan?.planType === "client";
  const effectivePlanTypeForUi =
    initialPlanType === "template" ? "template" : plan?.planType || "template";
  const effectiveClientUIDForUi =
    effectivePlanTypeForUi === "client" ? selectedClientUID || plan?.clientUID || "" : "";
  const isClientPlanMissingClient =
    effectivePlanTypeForUi === "client" && !effectiveClientUIDForUi;
  const currentPlanStatus = plan?.v2Meta?.status || plan?.status || "draft";
  const isLifecycleReadOnlyInEditor =
    !isCreate && normalizePlanLifecycleStatus(currentPlanStatus) === "archived";
  const isAssignedPlanLockedInEditor =
    !isCreate &&
    currentPlanStatus === "active" &&
    ((assignmentCoverage.primaryCount || 0) > 0 || (assignmentCoverage.alternativeCount || 0) > 0);
  const hasUnsavedChanges = hasUnsavedMetadata || hasUnsavedExercises || splitDraftDirty;
  const publishDisabledStaging =
    useEditorStaging && !hasStagingDoc && !hasUnsavedChanges;
  const saveStatusLabel = isPublishing
    ? "Publishing…"
    : isAutoSaving
    ? "Auto-saving..."
    : useEditorStaging && hasStagingDoc && !hasUnsavedChanges
      ? lastSavedAt
        ? `Staged · ${lastSavedAt.toLocaleTimeString()}`
        : "Staged (unpublished)"
      : hasUnsavedChanges
        ? "Unsaved changes"
        : lastSavedAt
          ? `Saved ${lastSavedAt.toLocaleTimeString()}`
          : "All changes saved";
  const saveStatusColor = isPublishing || isAutoSaving
    ? "secondary"
    : hasUnsavedChanges
      ? "warning"
      : useEditorStaging && hasStagingDoc
        ? "warning"
        : "success";

  const planTypeChipTooltip = React.useMemo(() => {
    return (plan?.planType || "template") === "client"
      ? "Client plan: owned by one client. Use for client-specific programming."
      : "Template plan: reusable from the library and assignable to clients.";
  }, [plan?.planType]);

  const versionStatusChipTooltip = React.useMemo(() => {
    const st = normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft");
    if (st === "draft") return "Draft: not published for assignment yet.";
    if (st === "active") return "Active: this version can be assigned and used as the live line.";
    if (st === "archived") return "Archived: read-only history; not used for new assignments.";
    return "Lifecycle state for this version (draft, active, or archived).";
  }, [plan?.v2Meta?.status, plan?.status]);

  const primaryAssignmentChipTooltip = React.useMemo(
    () =>
      `Clients with this plan as their primary training plan (${assignmentCoverage.primaryCount || 0}).`,
    [assignmentCoverage.primaryCount],
  );

  const alternativeAssignmentChipTooltip = React.useMemo(
    () =>
      `Clients with this plan as an alternative (${assignmentCoverage.alternativeCount || 0}).`,
    [assignmentCoverage.alternativeCount],
  );

  const versionNumberChipTooltip = React.useMemo(
    () =>
      `Version number in this plan's version history (V${plan?.v2Meta?.versionNumber ?? 0}).`,
    [plan?.v2Meta?.versionNumber],
  );

  const stagingModeChipTooltip =
    "Staging mode: this active plan is assigned to clients. Saves go to a staging copy until you Publish, which updates what clients see.";

  const saveStatusChipTooltip = React.useMemo(() => {
    if (isPublishing) return "Publishing staged changes to the live plan clients use.";
    if (isAutoSaving) return "Saving your edits in the background.";
    if (useEditorStaging && hasStagingDoc && !hasUnsavedChanges) {
      return "Staged snapshot saved. Publish to update the live plan for assigned clients.";
    }
    if (hasUnsavedChanges) {
      return "Unsaved edits in the editor. Save changes or wait for auto-save.";
    }
    if (lastSavedAt) {
      if (useEditorStaging) {
        return "Editor matches the published live plan. Further edits save to staging until you Publish.";
      }
      return `Last saved at ${lastSavedAt.toLocaleTimeString()}.`;
    }
    return "All changes are saved.";
  }, [
    isPublishing,
    isAutoSaving,
    useEditorStaging,
    hasStagingDoc,
    hasUnsavedChanges,
    lastSavedAt,
  ]);

  const handleRevertChanges = async () => {
    if (isCreate || !planId) {
      return;
    }
    if (!hasUnsavedChanges && !(useEditorStaging && hasStagingDoc)) {
      setMessage("Nothing to revert — there are no unsaved changes.", "warning");
      return;
    }
    const ok = window.confirm(
      useEditorStaging
        ? "Discard staged changes and reload the published live plan (what clients see)?"
        : "Discard unsaved changes and reload this plan from the last saved version?",
    );
    if (!ok) {
      return;
    }
    setIsReverting(true);
    try {
      if (useEditorStaging) {
        await deleteEditorStaging(planId);
      }
      await loadData({
        silent: true,
        preserveSplitId: selectedSplitId,
        reloadFromLiveOnly: useEditorStaging,
      });
      setLastSavedAt(new Date());
      setMessage(
        useEditorStaging ? "Reverted to published plan." : "Reverted to last saved version.",
      );
      trackTrainingPlansV2Event("plan_revert_changes", { planId, staging: useEditorStaging });
    } catch {
      // loadData already set message
    } finally {
      setIsReverting(false);
    }
  };

  if (loading) return <Typography>Loading editor...</Typography>;

  if (isLifecycleReadOnlyInEditor) {
    return (
      <Box sx={{ pb: { xs: 10, md: 14 } }}>
        {message ? (
          <Alert sx={{ mb: 2 }} severity={messageSeverity} onClose={() => setMessage("")}>
            {message}
          </Alert>
        ) : null}
        <Typography variant="h6" sx={{ mb: 1 }}>
          {plan?.name || "Training Plan"}{" "}
          {plan?.v2Meta?.versionNumber !== undefined && plan?.v2Meta?.versionNumber !== null
            ? `• V${plan.v2Meta.versionNumber}`
            : ""}
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This version is <strong>{currentPlanStatus}</strong> and read-only. Restore as draft (or
          create a new draft) using the Manage button in the Training Plans toolbar to make changes and
          publish.
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() =>
              navigate(
                withGuidedSearchPreserve(
                  `/home/training-plans-v2/${planId}/manage`,
                  location.search,
                ),
              )
            }
          >
            Open Manage
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              navigate(withGuidedSearchPreserve("/home/training-plans-v2/library", location.search))
            }
          >
            Back to Library
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, md: 14 } }}>
      {message ? (
        <Alert sx={{ mb: 2 }} severity={messageSeverity} onClose={() => setMessage("")}>
          {message}
        </Alert>
      ) : null}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ position: "sticky", top: 0, zIndex: 2, backgroundColor: "#F6F7FB", py: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Training Plan Name"
            value={plan?.name || ""}
            sx={{ minWidth: { xs: 260, md: 440 } }}
            onChange={(e) => setPlan((prev) => ({ ...prev, name: e.target.value }))}
          />
          <StatusChipHint title={planTypeChipTooltip}>
            <Chip label={getPlanTypeLabel(plan?.planType || "template")} size="small" />
          </StatusChipHint>
          <StatusChipHint title={versionStatusChipTooltip}>
            <Chip label={`Version Status: ${plan?.v2Meta?.status || plan?.status || "draft"}`} size="small" />
          </StatusChipHint>
          <StatusChipHint title={primaryAssignmentChipTooltip}>
            <Chip
              label={`Primary: ${assignmentCoverage.primaryCount || 0}`}
              size="small"
              variant="outlined"
              sx={getCoverageChipSx(assignmentCoverage.primaryCount || 0, "success")}
            />
          </StatusChipHint>
          <StatusChipHint title={alternativeAssignmentChipTooltip}>
            <Chip
              label={`Alternative: ${assignmentCoverage.alternativeCount || 0}`}
              size="small"
              variant="outlined"
              sx={getCoverageChipSx(assignmentCoverage.alternativeCount || 0, "warning")}
            />
          </StatusChipHint>
          <StatusChipHint title={versionNumberChipTooltip}>
            <Chip
              label={`V${plan?.v2Meta?.versionNumber ?? 0}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          </StatusChipHint>
          {useEditorStaging ? (
            <StatusChipHint title={stagingModeChipTooltip}>
              <Chip label="Staging" size="small" color="info" variant="outlined" />
            </StatusChipHint>
          ) : null}
          <StatusChipHint title={saveStatusChipTooltip}>
            <Chip
              label={saveStatusLabel}
              size="small"
              color={saveStatusColor}
              variant={hasUnsavedChanges ? "filled" : "outlined"}
            />
          </StatusChipHint>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {hints.hasAnyDismissedInScope ? (
            <Button variant="text" size="small" onClick={hints.restoreAll}>
              Show hints
            </Button>
          ) : null}
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleRevertChanges}
            disabled={
              isCreate ||
              (!hasUnsavedChanges && !(useEditorStaging && hasStagingDoc)) ||
              isReverting ||
              isAutoSaving ||
              isPublishing ||
              isClientPlanMissingClient
            }
          >
            Revert changes
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={isClientPlanMissingClient || isReverting || isPublishing}
          >
            Save changes
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={
              isClientPlanMissingClient ||
              isReverting ||
              isPublishing ||
              publishDisabledStaging ||
              (!assignmentCoverageReady &&
                normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "draft") ===
                  "active" &&
                ((assignmentCoverage.primaryCount || 0) > 0 ||
                  (assignmentCoverage.alternativeCount || 0) > 0))
            }
            startIcon={isPublishing ? <CircularProgress color="inherit" size={16} /> : null}
          >
            {isPublishing ? "Publishing…" : "Publish"}
          </Button>
          <IconButton
            aria-label="More training plan actions"
            aria-controls={overflowMenuAnchor ? "training-plan-editor-overflow-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={overflowMenuAnchor ? "true" : undefined}
            size="small"
            onClick={(event) => setOverflowMenuAnchor(event.currentTarget)}
            disabled={
              isCreate ||
              isClientPlanMissingClient ||
              isSavingAsDraftVersion ||
              isAutoSaving ||
              isReverting ||
              isPublishing
            }
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            id="training-plan-editor-overflow-menu"
            anchorEl={overflowMenuAnchor}
            open={Boolean(overflowMenuAnchor)}
            onClose={() => setOverflowMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={handleSaveAsNewDraftVersion}
              disabled={
                isSavingAsDraftVersion ||
                isReverting ||
                isPublishing ||
                (useEditorStaging &&
                  (hasStagingDoc ||
                    hasUnsavedMetadata ||
                    hasUnsavedExercises ||
                    splitDraftDirty))
              }
            >
              Save as new draft version
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {!isCreate && planId && (assignmentCoverage.alternativeCount || 0) > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 900 }}>
          To <strong>remove an alternative</strong> for a client, open{" "}
          <Button
            size="small"
            variant="text"
            sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: 700, verticalAlign: "baseline" }}
            onClick={() =>
              navigate(
                withGuidedSearchPreserve(
                  `/home/training-plans-v2/${planId}/assign`,
                  location.search,
                ),
              )
            }
          >
            Assign
          </Button>{" "}
          → <strong>Who has this plan</strong> → <strong>Remove alternative</strong> next to their name
          (their primary plan stays the same).
        </Typography>
      ) : null}

      <Stack direction="row" justifyContent="flex-start" sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowAdvancedSettings((prev) => !prev)}
        >
          {showAdvancedSettings ? "Hide Advanced Settings" : "Advanced Settings"}
        </Button>
      </Stack>

      {showAdvancedSettings ? (
        <Box sx={{ mt: 1, p: 2, border: "1px solid #ddd", borderRadius: 2, backgroundColor: "#fff" }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Plan Structure</Typography>
            <Typography variant="caption" color="text.secondary">
              These settings apply to the full training plan, not an individual workout.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Select
                size="small"
                value={plan?.type || "general"}
                onChange={(e) => setPlan((prev) => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="general">General Fitness</MenuItem>
                <MenuItem value="body_building">Body Building</MenuItem>
                <MenuItem value="strength">Strength</MenuItem>
                <MenuItem value="cross_fit">Cross Fit</MenuItem>
              </Select>
              <Select
                size="small"
                value={plan?.difficulty || "beginner"}
                onChange={(e) => setPlan((prev) => ({ ...prev, difficulty: e.target.value }))}
              >
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
                <MenuItem value="expert">Expert</MenuItem>
              </Select>
              <Select
                size="small"
                value={plan?.privacy || "private"}
                onChange={(e) => setPlan((prev) => ({ ...prev, privacy: e.target.value }))}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="organization">Organization</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </Select>
            </Stack>
            <Stack spacing={0.5}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                margin="none"
                label="Plan Description"
                helperText="Applies to the full training plan across all workouts."
                value={plan?.description || ""}
                onChange={(e) => setPlan((prev) => ({ ...prev, description: e.target.value }))}
                sx={{
                  "& .MuiFormHelperText-root": {
                    mt: 0.5,
                    mb: 0,
                  },
                }}
              />
              <Stack direction="row" justifyContent="flex-end">
                {canUseTrainingPlanAi ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AIIcon />}
                    onClick={handleGenerateDescription}
                    disabled={isGeneratingDescription || !splits.length}
                  >
                    {isGeneratingDescription ? "Generating..." : "AI Generate Description"}
                  </Button>
                ) : (
                  <UpgradePrompt
                    organizationData={organizationData}
                    minTier="pro"
                    variant="banner"
                    title="Upgrade for AI plan descriptions"
                    description="AI description assistance is available on Pro."
                    featureName="training_plan_data_entry_assistance"
                  />
                )}
              </Stack>
            </Stack>
            <Stack spacing={0.75} sx={{ pt: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                Plan labels (configured in Admin) use organization colors.
              </Typography>
              <LabelSelect
                id={`training-plans-v2-plan-${planId || "create"}`}
                currentLabels={plan?.protocolLabels || []}
                onLabelsChanged={({ protocolLabels }) => {
                  const nextIds = protocolLabels || [];
                  setPlan((prev) => (prev ? { ...prev, protocolLabels: [...nextIds] } : prev));
                }}
                width="100%"
                size="small"
                density="compact"
                disabled={isLifecycleReadOnlyInEditor}
              />
            </Stack>
          </Stack>
        </Box>
      ) : null}

      <Stack spacing={1} sx={{ mt: 1 }}>
        {!hints.isDismissed("version_status_assignment_coverage_info") ? (
          <Alert
            severity="info"
            onClose={() => hints.dismiss("version_status_assignment_coverage_info")}
          >
            Version Status and Assignment Coverage are separate. Publishing updates version lifecycle
            state; assigning determines which clients actively use this plan.
          </Alert>
        ) : null}
        {isAssignedPlanLockedInEditor && !hints.isDismissed("editor_locked_assigned") ? (
          <Alert
            severity="info"
            onClose={() => hints.dismiss("editor_locked_assigned")}
          >
            This version is assigned to clients. Use <strong>Publish</strong> to save your edits, create a
            new version, and migrate primary clients to it, or use the <strong>⋯</strong> menu to save as a
            new draft without publishing yet.
          </Alert>
        ) : null}
        {(plan?.v2Meta?.status || "draft") === "active" &&
        (assignmentCoverage.primaryCount || 0) === 0 &&
        !hints.isDismissed("editor_active_no_primary") ? (
          <Alert
            severity="warning"
            onClose={() => hints.dismiss("editor_active_no_primary")}
          >
            This version is active in metadata but has no primary client assignments yet. Use the Assign
            button in the Training Plans toolbar to deploy it.
          </Alert>
        ) : null}
        {(plan?.v2Meta?.status || "draft") === "draft" &&
        !hints.isDismissed("next_best_action_draft_info") ? (
          <Alert severity="info" onClose={() => hints.dismiss("next_best_action_draft_info")}>
            Next best action: finish edits, publish this version, then verify assignment coverage.
          </Alert>
        ) : null}
      </Stack>


      {effectivePlanTypeForUi === "client" ? (
        <Box sx={{ mt: 2, p: 2, border: "1px solid #ddd", borderRadius: 2, backgroundColor: "#fff" }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Client plan owner</Typography>
            {isClientPlanMissingClient && !hints.isDismissed("editor_client_plan_select_client") ? (
              <Alert
                severity="warning"
                onClose={() => hints.dismiss("editor_client_plan_select_client")}
              >
                Select the client this plan belongs to before saving or publishing.
              </Alert>
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showInactiveClients}
                  onChange={(e) => setShowInactiveClients(e.target.checked)}
                />
              }
              label="Include inactive clients"
            />
            <Autocomplete
              size="small"
              options={availableClients}
              value={selectedClient}
              disabled={isClientOwnerLocked}
              onChange={(_, value) => {
                if (!isClientOwnerLocked) {
                  setSelectedClientUID(value?.uid || "");
                }
              }}
              isOptionEqualToValue={(option, value) => option.uid === value.uid}
              getOptionLabel={(option) =>
                `${`${option.firstName || ""} ${option.lastName || ""}`.trim() || option.uid}${
                  option.status === "disabled" || option.status === "inactive" ? " (inactive)" : ""
                }`
              }
              renderInput={(params) => (
                <TextField {...params} label="Client (search by name)" />
              )}
            />
            {selectedClient ? (
              <Typography variant="caption" color="text.secondary">
                Selected client: {selectedClient.firstName || ""} {selectedClient.lastName || ""} (
                {selectedClient.uid})
              </Typography>
            ) : isClientOwnerLocked ? (
              <Typography variant="caption" color="text.secondary">
                Existing client plans stay with their owner client. Convert to reusable plan first to
                branch this plan to a different client via variant assignment.
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Select a client for this client-specific plan.
              </Typography>
            )}
          </Stack>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 35%) minmax(0, 65%)" },
          gap: 2,
          mt: 2,
        }}
      >
        <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, minWidth: 0 }}>
          <Typography variant="h6">Plan Structure</Typography>
          {planStructureBlockNotice ? (
            <Alert
              severity="error"
              sx={{ mt: 1, mb: 1 }}
              onClose={() => setPlanStructureBlockNotice("")}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  disabled={isClientPlanMissingClient || isReverting || isPublishing}
                  onClick={() => {
                    void handleSaveDraft();
                  }}
                >
                  Save now
                </Button>
              }
            >
              {planStructureBlockNotice}
            </Alert>
          ) : null}
          <Stack spacing={1.5} sx={{ mt: 1, mb: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleAddWorkout}>
                + Add Workout
              </Button>
              <Button size="small" variant="outlined" onClick={handleAddRestDay}>
                + Add Rest Day
              </Button>
              <Tooltip
                title={
                  effectiveClientUIDForUi
                    ? "Copy a workout (with exercises) from another plan for this client"
                    : "Only available on client plans"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleOpenCopyWorkoutDialog}
                    disabled={!effectiveClientUIDForUi || isLifecycleReadOnlyInEditor}
                  >
                    Copy from Workout
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            {!isCreate ? (
              canImportPlans ? (
                <>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                    style={{ display: "none" }}
                    ref={appendImportInputRef}
                    onChange={handleAppendImportFileChange}
                  />
                  <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadFileIcon fontSize="small" />}
                      disabled={
                        loading ||
                        appendImporting ||
                        isLifecycleReadOnlyInEditor ||
                        isAssignedPlanLockedInEditor
                      }
                      onClick={() => appendImportInputRef.current?.click()}
                      sx={{
                        width: "100%",
                        height: 40,
                        minHeight: 40,
                        boxSizing: "border-box",
                        py: 0,
                      }}
                    >
                      {appendImporting ? "Analyzing…" : "Import PDF / image (append)"}
                    </Button>
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{
                        minWidth: 0,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          minHeight: 40,
                          boxSizing: "border-box",
                        },
                      }}
                      disabled={
                        loading ||
                        appendImporting ||
                        isLifecycleReadOnlyInEditor ||
                        isAssignedPlanLockedInEditor
                      }
                    >
                      <InputLabel id="tp-v2-editor-import-format-label">Import format</InputLabel>
                      <Select
                        labelId="tp-v2-editor-import-format-label"
                        label="Import format"
                        value={normalizeTrainingPlanImportSource(importSource)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setImportSource(v);
                          try {
                            window.localStorage.setItem(TRAINING_PLAN_IMPORT_SOURCE_STORAGE_KEY, v);
                          } catch (_) {
                            /* ignore */
                          }
                        }}
                        renderValue={(v) => {
                          const o = TRAINING_PLAN_IMPORT_SOURCES.find((x) => x.id === v);
                          return (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <TuneIcon sx={{ fontSize: 18, opacity: 0.85 }} />
                              <span>{o?.label ?? v}</span>
                            </Stack>
                          );
                        }}
                      >
                        {TRAINING_PLAN_IMPORT_SOURCES.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            <ListItemText primary={opt.label} secondary={opt.description} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </>
              ) : (
                <UpgradePrompt
                  organizationData={organizationData}
                  minTier="pro"
                  variant="banner"
                  title="Upgrade to import training plans"
                  description="PDF and image import is available on Pro."
                  featureName="training_plan_imports"
                />
              )
            ) : null}
          </Stack>
          {!isCreate && canImportPlans && appendImportError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAppendImportError("")}>
              {appendImportError}
            </Alert>
          ) : null}
          {!isCreate &&
          canImportPlans &&
          (isLifecycleReadOnlyInEditor || isAssignedPlanLockedInEditor) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {isLifecycleReadOnlyInEditor
                ? "Append import is disabled for archived versions."
                : "Append import is disabled while this active version is assigned. Create a draft version to add workouts from a file."}
            </Typography>
          ) : null}
          {splits.length === 0 ? (
            <Typography color="text.secondary">No workouts yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {splits.map((split, splitIndex) => (
                <Box
                  key={split.id}
                  sx={(theme) => {
                    const isSelected = selectedSplitId === split.id;
                    const isRest = split.isRestSplit;
                    return {
                      borderRadius: 1,
                      p: 1.25,
                      cursor: "pointer",
                      transition:
                        "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                      backgroundColor: isRest
                        ? isSelected
                          ? alpha(theme.palette.grey[600], 0.14)
                          : alpha(theme.palette.grey[500], 0.1)
                        : isSelected
                          ? alpha(theme.palette.primary.main, 0.06)
                          : theme.palette.background.paper,
                      border: isSelected ? "2px solid" : "1px solid",
                      borderColor: isSelected
                        ? theme.palette.primary.main
                        : isRest
                          ? theme.palette.grey[300]
                          : theme.palette.divider,
                      boxShadow: isRest
                        ? `inset 4px 0 0 ${alpha(theme.palette.grey[600], 0.45)}`
                        : "none",
                    };
                  }}
                  onClick={() => refreshExercises(split.id)}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography fontWeight={600}>{split.name}</Typography>
                        {split.isVariantPath && !split.isRestSplit && (
                          <Chip label="Alt" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10, "& .MuiChip-label": { px: 0.75 } }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {split.isRestSplit ? "Rest Day" : split.isVariantPath ? "Variant Workout" : "Workout"}
                        {` • Position ${splitIndex + 1}`}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={splitIndex === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveSplit(split.id, "up");
                        }}
                      >
                        Up
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={splitIndex === splits.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveSplit(split.id, "down");
                        }}
                      >
                        Down
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab value="details" label="Workout Details" />
            <Tab value="exercises" label="Exercises" />
            <Tab value="settings" label="Settings" />
          </Tabs>
          <Divider sx={{ my: 2 }} />

          {activeTab === "details" && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  label={selectedSplit?.isRestSplit ? "Rest Day Name" : "Workout Name"}
                  value={splitNameDraft}
                  disabled={!selectedSplit}
                  sx={{ flex: 1, minWidth: 220, maxWidth: 420 }}
                  onChange={(e) => setSplitNameDraft(e.target.value)}
                  onBlur={flushSplitDraftOnBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void flushSplitDraftOnBlur();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleDuplicateWorkout}
                  disabled={!selectedSplit}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Duplicate Workout
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteWorkout}
                  disabled={!selectedSplit}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Delete Workout
                </Button>
              </Stack>
              <Stack spacing={0.5}>
                <TextField
                  fullWidth
                  margin="none"
                  label={selectedSplit?.isRestSplit ? "Rest Day Description" : "Workout Description"}
                  multiline
                  minRows={3}
                  value={splitDescriptionDraft}
                  disabled={!selectedSplit}
                  helperText="Applies only to the selected workout. Name and description save automatically."
                  onChange={(e) => setSplitDescriptionDraft(e.target.value)}
                  onBlur={flushSplitDraftOnBlur}
                  sx={{
                    "& .MuiFormHelperText-root": {
                      mt: 0.5,
                      mb: 0,
                    },
                  }}
                />
                <Stack direction="row" justifyContent="flex-end">
                  {canUseTrainingPlanAi ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AIIcon />}
                      onClick={handleGenerateWorkoutDescription}
                      disabled={
                        !selectedSplit ||
                        isGeneratingWorkoutDescription ||
                        (!selectedSplit?.isRestSplit && exercises.length === 0)
                      }
                    >
                      {isGeneratingWorkoutDescription
                        ? "Generating..."
                        : "AI Generate Workout Description"}
                    </Button>
                  ) : (
                    <UpgradePrompt
                      organizationData={organizationData}
                      minTier="pro"
                      variant="banner"
                      title="Upgrade for AI workout descriptions"
                      description="AI description assistance is available on Pro."
                      featureName="training_plan_data_entry_assistance"
                    />
                  )}
                </Stack>
              </Stack>
              {selectedSplit ? (
                <Stack spacing={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    Workout labels (configured in Admin) use organization colors.
                  </Typography>
                  <LabelSelect
                    id={`training-plans-v2-split-${planId}-${selectedSplit.id}`}
                    currentLabels={splitLabelsDraft}
                    onLabelsChanged={(changes) => {
                      setSplitLabelsDraft(Array.isArray(changes.protocolLabels) ? [...changes.protocolLabels] : []);
                    }}
                    width="100%"
                    size="small"
                    density="compact"
                    disabled={isLifecycleReadOnlyInEditor || !planId}
                  />
                </Stack>
              ) : null}
              {selectedSplit && !selectedSplit.isRestSplit && (
                <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 1.5, p: 1.5, backgroundColor: "#FAFAFA" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={splitVariantPathDraft}
                        onChange={(e) => {
                          setSplitVariantPathDraft(e.target.checked);
                        }}
                        onBlur={flushSplitDraftOnBlur}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Variant Path</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Mark as an alternative option — excluded from the main workout sequence and shown separately in Switch Workout.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
            </Stack>
          )}

          {activeTab === "exercises" && (
            selectedSplit?.isRestSplit ? (
              !hints.isDismissed("editor_rest_split_no_exercises") ? (
                <Alert
                  severity="info"
                  sx={{ py: 2 }}
                  onClose={() => hints.dismiss("editor_rest_split_no_exercises")}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Rest days don&apos;t include exercises
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This day is for recovery only. Use the <strong>Workout Details</strong> tab to name and
                    describe it. Training exercises and supersets are added on training workouts—not on rest
                    days.
                  </Typography>
                </Alert>
              ) : null
            ) : (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                <Button variant="contained" size="small" onClick={handleAddExercise}>Add Exercise</Button>
                <Button variant="outlined" size="small" onClick={handleAddSuperset}>Add Superset</Button>
                <Button variant="outlined" size="small" onClick={() => setShowBulkAddDialog(true)}>
                  Bulk Add
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={toggleSelectAllExercises}
                  disabled={!exercises.length}
                >
                  {selectedExerciseIds.length > 0 ? "Clear Selection" : "Select All"}
                </Button>
                <Chip
                  size="small"
                  label={`${selectedExerciseIds.length} selected`}
                  color={selectedExerciseIds.length ? "primary" : "default"}
                  variant={selectedExerciseIds.length ? "filled" : "outlined"}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleOpenBulkEditDialog}
                  disabled={!selectedExerciseIds.length}
                >
                  Bulk Edit Values
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleBulkDeleteSelected}
                  disabled={!selectedExerciseIds.length}
                >
                  Bulk Delete
                </Button>
              </Stack>

              {exercises.length === 0 ? (
                <Box sx={{ p: 2, border: "1px dashed #bbb", borderRadius: 1 }}>
                  <Typography>No exercises in this workout yet</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {exercises.map((exercise, exerciseIndex) => (
                    <Box
                      key={exercise.id}
                      ref={(node) => {
                        if (node) {
                          exerciseRowRefs.current[exercise.id] = node;
                        } else {
                          delete exerciseRowRefs.current[exercise.id];
                        }
                      }}
                      sx={{
                        border: "1px solid #E2E8F0",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Checkbox
                              checked={selectedExerciseIds.includes(exercise.id)}
                              onChange={() => toggleExerciseSelection(exercise.id)}
                              inputProps={{ "aria-label": `Select exercise row ${exerciseIndex + 1}` }}
                            />
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {exercise.type === WorkoutTypes.superSet
                                  ? exercise.superSetName || "Superset"
                                  : getExerciseNameById(exercise.exercise, exercise.exerciseName)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {exercise.type === WorkoutTypes.superSet
                                  ? "Superset group"
                                  : "Single exercise"}
                                {` • Position ${exerciseIndex + 1}`}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              backgroundColor: "#fff",
                              py: 0.5,
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDuplicateExercise(exercise.id)}
                            >
                              Duplicate
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={exerciseIndex === 0}
                              onClick={() => handleMoveExercise(exercise.id, "up")}
                            >
                              Up
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={exerciseIndex === exercises.length - 1}
                              onClick={() => handleMoveExercise(exercise.id, "down")}
                            >
                              Down
                            </Button>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <InputLabel>Set Type</InputLabel>
                              <Select
                                label="Set Type"
                                value={exercise.type || WorkoutTypes.normalSet}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setExercises((prev) =>
                                    prev.map((x) => {
                                      if (x.id !== exercise.id) return x;
                                      if (value === WorkoutTypes.superSet) {
                                        return {
                                          ...x,
                                          type: value,
                                          exercise: WorkoutTypes.superSet,
                                          superSetName: x.superSetName || "Superset",
                                          exercises:
                                            x.exercises && x.exercises.length > 0
                                              ? x.exercises
                                              : [createSupersetChild()],
                                        };
                                      }
                                      return {
                                        ...x,
                                        type: value,
                                        exercise:
                                          x.exercise === WorkoutTypes.superSet ? null : x.exercise,
                                        exercises: [],
                                      };
                                    }),
                                  );
                                }}
                              >
                                <MenuItem value={WorkoutTypes.normalSet}>Normal Set</MenuItem>
                                <MenuItem value={WorkoutTypes.superSet}>Super Set</MenuItem>
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                setShowAdvancedByExercise((prev) => ({
                                  ...prev,
                                  [exercise.id]: !prev[exercise.id],
                                }))
                              }
                            >
                              {showAdvancedByExercise[exercise.id] ? "Hide Advanced" : "Show Advanced"}
                            </Button>
                          </Stack>
                        </Stack>

                        {exercise.type !== WorkoutTypes.superSet ? (
                          <Box>
                            <ExerciseSearchAutocomplete
                              exercises={exerciseOptions}
                              value={exercise.exercise || null}
                              label="Exercise"
                              placeholder="Search exercise by name..."
                              onCreateNew={handleCreateCustomExercise}
                              onChange={(exerciseId, exerciseName) => {
                                setExercises((prev) =>
                                  prev.map((x) =>
                                    x.id === exercise.id
                                      ? {
                                          ...x,
                                          exercise: exerciseId || null,
                                          ...(exerciseId
                                            ? { exerciseName: exerciseName || x.exerciseName }
                                            : { exerciseName: null }),
                                        }
                                      : x,
                                  ),
                                );
                              }}
                            />
                          </Box>
                        ) : (
                          <TextField
                            size="small"
                            label="Superset Name"
                            value={exercise.superSetName || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setExercises((prev) =>
                                prev.map((x) =>
                                  x.id === exercise.id ? { ...x, superSetName: value } : x,
                                ),
                              );
                            }}
                          />
                        )}

                        <Box
                          sx={{
                            display: "grid",
                            gap: 1,
                            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                          }}
                        >
                          <TextField
                            size="small"
                            label="Sets"
                            type="number"
                            value={exercise.sets || 0}
                            onChange={(e) => {
                              const value = parseInt(e.target.value || "0", 10);
                              setExercises((prev) =>
                                prev.map((x) => (x.id === exercise.id ? { ...x, sets: value } : x)),
                              );
                            }}
                          />
                          <TextField
                            size="small"
                            label="Rest"
                            placeholder="e.g. 60s"
                            value={exercise.rest || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setExercises((prev) =>
                                prev.map((x) => (x.id === exercise.id ? { ...x, rest: value } : x)),
                              );
                            }}
                          />
                          {exercise.type !== WorkoutTypes.superSet ? (
                            <FormControl size="small">
                              <InputLabel>Prescription</InputLabel>
                              <Select
                                label="Prescription"
                                value={exercise.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setExercises((prev) =>
                                    prev.map((x) =>
                                      x.id === exercise.id ? { ...x, prescriptionType: value } : x,
                                    ),
                                  );
                                }}
                              >
                                {Object.values(ExercisePrescriptionTypes).map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {prescriptionLabelByType[type] || type}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : null}
                        </Box>

                        {exercise.type !== WorkoutTypes.superSet ? (
                          <Box>{renderPrescriptionFields(exercise)}</Box>
                        ) : (
                          <Box
                            sx={{
                              border: "1px solid #E2E8F0",
                              borderRadius: 2,
                              p: 1.5,
                              backgroundColor: "#F8FAFC",
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Superset Exercises</Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => addSupersetChild(exercise.id)}
                              >
                                Add Exercise
                              </Button>
                            </Stack>
                            <Stack spacing={1}>
                              {(exercise.exercises || []).map((child, childIndex) => (
                                <Box
                                  key={child.id || `${exercise.id}-child-${childIndex}`}
                                  sx={{
                                    border: "1px solid #E2E8F0",
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <Stack spacing={1}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Typography variant="body2" fontWeight={600}>
                                        Exercise #{childIndex + 1}
                                      </Typography>
                                      <Stack direction="row" spacing={1}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={childIndex === 0}
                                          onClick={() => moveSupersetChild(exercise.id, child.id, "up")}
                                        >
                                          Up
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={childIndex === (exercise.exercises || []).length - 1}
                                          onClick={() => moveSupersetChild(exercise.id, child.id, "down")}
                                        >
                                          Down
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => duplicateSupersetChild(exercise.id, child.id)}
                                        >
                                          Duplicate
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => removeSupersetChild(exercise.id, child.id)}
                                        >
                                          Remove
                                        </Button>
                                      </Stack>
                                    </Stack>
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gap: 1,
                                        gridTemplateColumns: "2fr minmax(160px, 1fr)",
                                      }}
                                    >
                                      <ExerciseSearchAutocomplete
                                        exercises={exerciseOptions}
                                        value={child.exercise || null}
                                        label="Superset Exercise"
                                        placeholder="Search exercise by name..."
                                        onCreateNew={handleCreateCustomExercise}
                                        onChange={(exerciseId, exerciseName) => {
                                          updateSupersetChild(exercise.id, child.id, {
                                            exercise: exerciseId || null,
                                            ...(exerciseId
                                              ? { exerciseName: exerciseName || child.exerciseName }
                                              : { exerciseName: null }),
                                          });
                                        }}
                                      />
                                      <FormControl size="small">
                                        <InputLabel>Prescription</InputLabel>
                                        <Select
                                          label="Prescription"
                                          value={
                                            child.prescriptionType ||
                                            DEFAULT_EXERCISE_PRESCRIPTION_TYPE
                                          }
                                          onChange={(e) => {
                                            updateSupersetChild(exercise.id, child.id, {
                                              prescriptionType: e.target.value,
                                            });
                                          }}
                                        >
                                          {Object.values(ExercisePrescriptionTypes).map((type) => (
                                            <MenuItem key={type} value={type}>
                                              {prescriptionLabelByType[type] || type}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gap: 1,
                                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                        alignItems: "start",
                                      }}
                                    >
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Rest"
                                        placeholder="e.g. 30s"
                                        value={child.rest || ""}
                                        onChange={(e) => {
                                          updateSupersetChild(exercise.id, child.id, {
                                            rest: e.target.value,
                                          });
                                        }}
                                      />
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Tempo"
                                        value={child.tempo || ""}
                                        onChange={(e) => {
                                          updateSupersetChild(exercise.id, child.id, {
                                            tempo: e.target.value,
                                          });
                                        }}
                                      />
                                      {renderSupersetChildPrescriptionFields(exercise.id, child)}
                                    </Box>
                                    <TextField
                                      size="small"
                                      label="Notes"
                                      multiline
                                      minRows={2}
                                      value={child.notes || ""}
                                      onChange={(e) => {
                                        updateSupersetChild(exercise.id, child.id, {
                                          notes: e.target.value,
                                        });
                                      }}
                                    />
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {showAdvancedByExercise[exercise.id] ? (
                          <Box
                            sx={{
                              border: "1px dashed #CBD5E1",
                              borderRadius: 1.5,
                              p: 1.5,
                              backgroundColor: "#FAFAFA",
                            }}
                          >
                            <Stack spacing={1}>
                              <Box
                                sx={{
                                  display: "grid",
                                  gap: 1,
                                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                                }}
                              >
                                <TextField
                                  size="small"
                                  label="Sets to Failure"
                                  type="number"
                                  value={exercise.setsToFailure || 0}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value || "0", 10);
                                    setExercises((prev) =>
                                      prev.map((x) =>
                                        x.id === exercise.id ? { ...x, setsToFailure: value } : x,
                                      ),
                                    );
                                  }}
                                />
                                <TextField
                                  size="small"
                                  label="Tempo"
                                  value={exercise.tempo || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setExercises((prev) =>
                                      prev.map((x) => (x.id === exercise.id ? { ...x, tempo: value } : x)),
                                    );
                                  }}
                                />
                              </Box>
                              <Stack spacing={0.35} sx={{ mb: -0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Exercise labels
                                </Typography>
                                <LabelSelect
                                  id={`training-plans-v2-exercise-${planId}-${selectedSplitId}-${exercise.id}`}
                                  currentLabels={exercise.labels || []}
                                  onLabelsChanged={({ protocolLabels }) => {
                                    const nextIds = protocolLabels || [];
                                    setExercises((prev) =>
                                      prev.map((x) =>
                                        x.id === exercise.id ? { ...x, labels: [...nextIds] } : x,
                                      ),
                                    );
                                  }}
                                  width="100%"
                                  size="small"
                                  density="compact"
                                  disabled={isLifecycleReadOnlyInEditor}
                                />
                              </Stack>
                              <TextField
                                size="small"
                                label="Notes"
                                multiline
                                minRows={2}
                                value={exercise.notes || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setExercises((prev) =>
                                    prev.map((x) => (x.id === exercise.id ? { ...x, notes: value } : x)),
                                  );
                                }}
                              />
                            </Stack>
                          </Box>
                        ) : null}

                        {/* Alternatives section */}
                        <Box
                          sx={{
                            border: "1px solid #C7D2FE",
                            borderRadius: 1.5,
                            backgroundColor: "#EEF2FF",
                            overflow: "hidden",
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ px: 1.5, py: 1, cursor: "pointer" }}
                            onClick={() =>
                              setShowAlternativesByExercise((prev) => ({
                                ...prev,
                                [exercise.id]: !prev[exercise.id],
                              }))
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" fontWeight={600} color="primary">
                                Alternative Exercises
                              </Typography>
                              {(exercise.alternatives || []).length > 0 && (
                                <Chip
                                  label={(exercise.alternatives || []).length}
                                  size="small"
                                  color="primary"
                                  sx={{ height: 18, fontSize: 10, "& .MuiChip-label": { px: 0.75 } }}
                                />
                              )}
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Button
                                size="small"
                                variant="text"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addAlternative(exercise.id);
                                }}
                              >
                                + Add Alternative
                              </Button>
                              <Typography variant="caption" color="primary">
                                {showAlternativesByExercise[exercise.id] ? "▲" : "▼"}
                              </Typography>
                            </Stack>
                          </Stack>
                          {showAlternativesByExercise[exercise.id] && (
                            <Box sx={{ px: 1.5, pb: 1.5, backgroundColor: "#FFFFFF" }}>
                              {(exercise.alternatives || []).length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  No alternatives set. Add one above so clients can swap this exercise if needed.
                                </Typography>
                              ) : (
                                <Stack spacing={1.5}>
                                  {(exercise.alternatives || []).map((alt, altIdx) => (
                                    <Box
                                      key={alt.id}
                                      sx={{
                                        border: "1px solid #E0E7FF",
                                        borderRadius: 1.5,
                                        p: 1.5,
                                        backgroundColor: "#F5F7FF",
                                      }}
                                    >
                                      <Stack spacing={1}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                          <Typography variant="caption" fontWeight={600} color="primary">
                                            Alternative {altIdx + 1}
                                          </Typography>
                                          <Stack direction="row" spacing={0.5}>
                                            <Button size="small" variant="text" disabled={altIdx === 0} onClick={() => moveAlternative(exercise.id, alt.id, "up")}>↑</Button>
                                            <Button size="small" variant="text" disabled={altIdx === (exercise.alternatives || []).length - 1} onClick={() => moveAlternative(exercise.id, alt.id, "down")}>↓</Button>
                                            <Button size="small" variant="text" color="error" onClick={() => removeAlternative(exercise.id, alt.id)}>Remove</Button>
                                          </Stack>
                                        </Stack>
                                        {/* Alt type selector */}
                                        <TextField
                                          select
                                          size="small"
                                          label="Type"
                                          value={alt.type || WorkoutTypes.normalSet}
                                          onChange={(e) => {
                                            const nextType = e.target.value;
                                            updateAlternative(exercise.id, alt.id, {
                                              type: nextType,
                                              exercises: nextType === WorkoutTypes.superSet && !(alt.exercises || []).length
                                                ? [createSupersetChild(), createSupersetChild()]
                                                : (alt.exercises || []),
                                            });
                                          }}
                                        >
                                          <MenuItem value={WorkoutTypes.normalSet}>Normal Set</MenuItem>
                                          <MenuItem value={WorkoutTypes.superSet}>Superset</MenuItem>
                                        </TextField>
                                        {alt.type !== WorkoutTypes.superSet ? (
                                          <>
                                            <ExerciseSearchAutocomplete
                                              exercises={exerciseOptions}
                                              value={alt.exercise || null}
                                              label="Exercise"
                                              placeholder="Search exercise by name..."
                                              onCreateNew={handleCreateCustomExercise}
                                              onChange={(exerciseId, exerciseName) => {
                                                updateAlternative(exercise.id, alt.id, {
                                                  exercise: exerciseId || null,
                                                  ...(exerciseId ? { exerciseName: exerciseName || alt.exerciseName } : { exerciseName: null }),
                                                });
                                              }}
                                            />
                                            {/* Prescription type selector */}
                                            <TextField
                                              select
                                              size="small"
                                              label="Prescription"
                                              value={alt.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE}
                                              onChange={(e) =>
                                                updateAlternative(exercise.id, alt.id, { prescriptionType: e.target.value })
                                              }
                                            >
                                              {Object.entries(prescriptionLabelByType).map(([value, label]) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                              ))}
                                            </TextField>
                                            {renderAlternativePrescriptionFields(exercise.id, alt)}
                                          </>
                                        ) : (
                                          <>
                                            <TextField
                                              size="small"
                                              label="Superset Name"
                                              value={alt.superSetName || ""}
                                              onChange={(e) => updateAlternative(exercise.id, alt.id, { superSetName: e.target.value })}
                                            />
                                            <Stack spacing={1}>
                                              {(alt.exercises || []).map((child, childIdx) => (
                                                <Box key={child.id} sx={{ border: "1px solid #E2E8F0", borderRadius: 1, p: 1, backgroundColor: "#FFF" }}>
                                                  <Stack spacing={1}>
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                      <Typography variant="caption" color="text.secondary">Exercise {childIdx + 1}</Typography>
                                                      {(alt.exercises || []).length > 1 && (
                                                        <IconButton size="small" onClick={() => removeAlternativeSupersetChild(exercise.id, alt.id, child.id)}>
                                                          <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                      )}
                                                    </Stack>
                                                    <ExerciseSearchAutocomplete
                                                      exercises={exerciseOptions}
                                                      value={child.exercise || null}
                                                      label="Exercise"
                                                      placeholder="Search exercise..."
                                                      onCreateNew={handleCreateCustomExercise}
                                                      onChange={(exerciseId, exerciseName) =>
                                                        updateAlternativeSupersetChild(exercise.id, alt.id, child.id, {
                                                          exercise: exerciseId || null,
                                                          ...(exerciseId ? { exerciseName: exerciseName || child.exerciseName } : { exerciseName: null }),
                                                        })
                                                      }
                                                    />
                                                    <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
                                                      <TextField size="small" label="Reps" value={child.reps || ""} onChange={(e) => updateAlternativeSupersetChild(exercise.id, alt.id, child.id, { reps: e.target.value })} />
                                                      <TextField size="small" label="Rest" value={child.rest || ""} onChange={(e) => updateAlternativeSupersetChild(exercise.id, alt.id, child.id, { rest: e.target.value })} />
                                                    </Box>
                                                  </Stack>
                                                </Box>
                                              ))}
                                              <Button size="small" variant="text" onClick={() => addAlternativeSupersetChild(exercise.id, alt.id)}>+ Add Exercise to Superset</Button>
                                            </Stack>
                                            {/* Sets + rest for the superset as a whole */}
                                            <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
                                              <TextField size="small" label="Sets" type="number" value={alt.sets || 0} onChange={(e) => updateAlternative(exercise.id, alt.id, { sets: parseInt(e.target.value || "0", 10) })} />
                                              <TextField size="small" label="Rest (between rounds)" value={alt.rest || ""} onChange={(e) => updateAlternative(exercise.id, alt.id, { rest: e.target.value })} />
                                            </Box>
                                          </>
                                        )}
                                        <TextField
                                          size="small"
                                          label="Notes (optional)"
                                          multiline
                                          minRows={2}
                                          value={alt.notes || ""}
                                          onChange={(e) => updateAlternative(exercise.id, alt.id, { notes: e.target.value })}
                                        />
                                      </Stack>
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          )}
                        </Box>

                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={async () => {
                              await handleDeleteExerciseRow(exercise.id);
                            }}
                          >
                            Delete
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              await handleSaveExerciseRow(exercise);
                            }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Button variant="contained" size="small" onClick={handleAddExercise}>
                  Add Exercise
                </Button>
                <Button variant="outlined" size="small" onClick={handleAddSuperset}>
                  Add Superset
                </Button>
              </Stack>
              <Dialog
                open={showBulkAddDialog}
                onClose={() => setShowBulkAddDialog(false)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Bulk Add Exercise Rows</DialogTitle>
                <DialogContent>
                  <Stack spacing={1.5} sx={{ pt: 1 }}>
                    <Autocomplete
                      options={exerciseOptions}
                      value={
                        exerciseOptions.find((exercise) => exercise.id === bulkAddDraftExerciseId) || null
                      }
                      getOptionLabel={(option) => option.name || option.id}
                      onChange={(_, value) => setBulkAddDraftExerciseId(value?.id || null)}
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="Exercise" placeholder="Search exercise" />
                      )}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddToBulkQueue}
                        disabled={!bulkAddDraftExerciseId}
                      >
                        Queue Exercise
                      </Button>
                      <Chip
                        size="small"
                        label={`${bulkAddQueue.length} queued`}
                        color={bulkAddQueue.length ? "primary" : "default"}
                        variant={bulkAddQueue.length ? "filled" : "outlined"}
                      />
                    </Stack>
                    <List
                      dense
                      sx={{
                        maxHeight: 220,
                        overflowY: "auto",
                        border: "1px solid #E2E8F0",
                        borderRadius: 1,
                      }}
                    >
                      {bulkAddQueue.length ? (
                        bulkAddQueue.map((queued, index) => (
                          <ListItem
                            key={`${queued.id}-${index}`}
                            secondaryAction={
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveBulkQueueItem(index)}
                              >
                                Remove
                              </Button>
                            }
                          >
                            <ListItemText primary={`${index + 1}. ${queued.name || queued.id}`} />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary="No exercises queued yet." />
                        </ListItem>
                      )}
                    </List>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowBulkAddDialog(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleApplyBulkAddQueue}
                    disabled={!bulkAddQueue.length}
                  >
                    Add Queued Rows
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={showBulkEditDialog}
                onClose={() => setShowBulkEditDialog(false)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Bulk Edit Value Fields</DialogTitle>
                <DialogContent>
                  <Stack spacing={1.5} sx={{ pt: 1 }}>
                    {!hints.isDismissed("editor_bulk_edit_value_fields_info") ? (
                      <Alert
                        severity="info"
                        onClose={() => hints.dismiss("editor_bulk_edit_value_fields_info")}
                      >
                        Bulk edit only updates value fields (sets, reps, rest, tempo, notes). Exercise
                        type and prescription type are intentionally excluded.
                      </Alert>
                    ) : null}
                    <FormControl size="small">
                      <InputLabel>Preset</InputLabel>
                      <Select
                        label="Preset"
                        value={bulkEditPreset}
                        onChange={(e) => handleBulkPresetChange(e.target.value)}
                      >
                        <MenuItem value="none">No Preset</MenuItem>
                        {Object.entries(BULK_EDIT_PRESETS).map(([key]) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1,
                        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Sets"
                        value={bulkEditDraft.sets}
                        onChange={(e) =>
                          setBulkEditDraft((prev) => ({ ...prev, sets: e.target.value }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Reps"
                        value={bulkEditDraft.reps}
                        onChange={(e) =>
                          setBulkEditDraft((prev) => ({ ...prev, reps: e.target.value }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Rest"
                        value={bulkEditDraft.rest}
                        onChange={(e) =>
                          setBulkEditDraft((prev) => ({ ...prev, rest: e.target.value }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Tempo"
                        value={bulkEditDraft.tempo}
                        onChange={(e) =>
                          setBulkEditDraft((prev) => ({ ...prev, tempo: e.target.value }))
                        }
                      />
                    </Box>
                    <TextField
                      size="small"
                      label="Notes"
                      multiline
                      minRows={2}
                      value={bulkEditDraft.notes}
                      onChange={(e) =>
                        setBulkEditDraft((prev) => ({ ...prev, notes: e.target.value }))
                      }
                    />
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowBulkEditDialog(false)}>Cancel</Button>
                  <Button variant="contained" onClick={handleApplyBulkEdit}>
                    Apply to {selectedExerciseIds.length} Row(s)
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
            )
          )}

          {activeTab === "settings" && (
            <Stack spacing={2}>
              {!hints.isDismissed("editor_settings_publish_state") ? (
                <Alert
                  severity="info"
                  onClose={() => hints.dismiss("editor_settings_publish_state")}
                >
                  Draft/Publish state controls edit readiness. Draft means in progress. Publish marks the
                  plan active for assignment workflows. Every save/publish action writes a revision record
                  so version history remains auditable from Manage (open Manage in the Training Plans
                  toolbar).
                </Alert>
              ) : null}
              {!hints.isDismissed("editor_settings_reusable_type") ? (
                <Alert
                  severity="warning"
                  onClose={() => hints.dismiss("editor_settings_reusable_type")}
                >
                  Reusable plans created from the library stay reusable. For a <strong>client plan</strong>,
                  change <strong>Plan Type</strong> below to <strong>Reusable Plan</strong> to promote this
                  document (then save/publish), or use <strong>Library → Duplicate as reusable plan</strong>{" "}
                  to keep the client copy and work from a new reusable duplicate.
                </Alert>
              ) : null}
              <FormControl size="small" sx={{ maxWidth: 320 }}>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  label="Plan Type"
                  value={plan?.planType || "template"}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    if (initialPlanType === "template" && nextType === "client") {
                      setMessage(
                        "Reusable plans cannot be converted to client plans. Create a new client plan from the library.",
                        "error",
                      );
                      return;
                    }
                    setPlan((prev) => ({ ...prev, planType: nextType }));
                    if (nextType === "template") {
                      setSelectedClientUID("");
                    }
                  }}
                >
                  <MenuItem value="template">{getPlanTypeLabel("template")}</MenuItem>
                  <MenuItem
                    value="client"
                    disabled={initialPlanType === "template"}
                  >
                    {getPlanTypeLabel("client")}
                  </MenuItem>
                </Select>
              </FormControl>

              {plan?.planType === "client" ? (
                <Typography variant="caption" color="text.secondary">
                  Client selection is required and shown above the editor for better visibility.
                </Typography>
              ) : null}
            </Stack>
          )}
        </Box>
      </Box>

      <CopyWorkoutFromPlanDialog
        open={showCopyWorkoutDialog}
        onClose={() => setShowCopyWorkoutDialog(false)}
        onConfirm={handleCopyWorkoutFromPlan}
        currentPlanId={planId}
        clientUID={effectiveClientUIDForUi}
      />
    </Box>
  );
};

export default TrainingPlansV2Editor;
